import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Save,
  Download,
  ArrowLeft,
  Trash2,
  Settings,
  AlertTriangle,
  Plus,
  HelpCircle,
  User,
  Mail,
  X,
  ChevronRight,
  Share2,
  Lock,
  ShieldCheck,
  Share,
  RefreshCw,
} from "lucide-react";
import {
  pad2,
  calculateDueDate,
  formatDate,
  renderInvoiceFromTemplate,
  formatAmount,
  getDaysDifference,
  isValidIco,
} from "./helpers";
import { generateAndDownloadPdf } from "./utils/pdfGenerator";
import { authenticatedFetch } from "./config/api";
import ClientDialog from "./ClientDialog";
import ItemsDialog from "./ItemsDialog";
import PaymentMethodDialog from "./PaymentMethodDialog";
import DesignPanel from "./DesignPanel";
import ItemsEditor from "./ItemsEditor";
import Toggle from "./Toggle";
import InvoiceNumberTemplate from "./InvoiceNumberTemplate";
import CustomDropdown from "./components/CustomDropdown";
import SearchableDropdown from "./components/SearchableDropdown";
import SaveButton from "./components/SaveButton";
import SettingsDialog from "./components/SettingsDialog";
import OnboardingTour from "./OnboardingTour";
import EmailConfirmationModal from "./components/EmailConfirmationModal";
import { getSoftTemplate } from "./utils/emailTemplates";

// Import translations, constants, types and styles
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { invoiceTranslations } from "./translations/invoiceTranslations";
import {
  LS_DRAFT_KEY,
  LS_DUE_DAYS_KEY,
  LS_LANGUAGE_KEY,
  LS_CLIENTS_KEY,
  LS_PAYMENT_METHODS_KEY,
  LS_ONBOARDING_COMPLETED,
  DEFAULT_DUE_DAYS,
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  languages,
} from "./constants";
import {
  Client,
  Item,
  CustomTax,
  PaymentOption,
  SavedPaymentMethod,
  SellerInfo,
  Draft,
  LanguageCode,
  InvoiceListItem,
} from "./types";
import { currencyNames } from "./currencyNames";
import {
  getCurrencyLabel,
  getSortedCurrencies,
} from "./utils/currencyLocalization";
import { sanitizeInput } from "./utils/sanitize";
import "./styles.css";

// Single-file React app that generates printable invoices and can download a clean PDF via backend server using Playwright.

export default function InvoiceApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isBlockerActive } = useOutletContext<{
    isBlockerActive: boolean;
  }>() || {
    isBlockerActive: false,
  };

  // --- 1. Date Helpers
  const today = useMemo(() => new Date(), []);
  const y = today.getFullYear();
  const mm = pad2(today.getMonth() + 1);
  const dd = pad2(today.getDate());
  const yy = String(y).slice(-2);

  // --- 2. Interface translations (from locale context)
  const t = useTranslation();
  const { locale } = useLocale();

  // --- 3. Base states
  const [invoiceLanguage, setInvoiceLanguage] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem(LS_LANGUAGE_KEY);
    return (saved as LanguageCode) || "en";
  });

  const [dueDays, setDueDays] = useState<number>(() => {
    const saved = localStorage.getItem(LS_DUE_DAYS_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_DUE_DAYS; // default 14 days
  });

  const [invoiceInternalId, setInvoiceInternalId] = useState<string>("");
  const [isEmailConfigured, setIsEmailConfigured] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [emailConfigLoaded, setEmailConfigLoaded] = useState<boolean>(false);

  // --- 4. Dependent constants
  const currentInvoiceT = invoiceTranslations[invoiceLanguage];

  const emptyDraft: Draft = {
    invoiceNo: "", // Will be generated when creating new invoice
    issueDate: `${y}-${mm}-${dd}`,
    dueDate: calculateDueDate(`${y}-${mm}-${dd}`, dueDays),
    currency: DEFAULT_CURRENCY,
    buyerName: "",
    buyerAddress: "",
    buyerEmail: "",
    buyerPhone: "",
    buyerTaxId: "",
    buyerId: "",
    buyerIsVatPayer: null,
    items: [{ id: crypto.randomUUID(), description: "", amount: 0 }], // One empty item by default
    note: "",
    paymentInfo: {
      accountNumber: "",
      iban: "",
      bic: "",
      swift: "",
      bankName: "",
      bankAddress: "",
      variableSymbol: "",
      constantSymbol: "",
    },
    paymentLink: { url: "", text: "" },
    paymentOptions: [
      {
        id: crypto.randomUUID(),
        type: "bank",
        title:
          invoiceTranslations[DEFAULT_LANGUAGE as LanguageCode]?.bankTransfer ||
          "Bank Transfer",
        details: "",
        accountNumber: "",
        iban: "",
        bic: "",
        bankName: "",
        bankAddress: "",
        variableSymbol: "",
        constantSymbol: "",
        specificSymbol: "",
        showQRCode: false,
      },
    ],
    sellerInfo: {
      name: "",
      address: "",
      country: "",
      notes: "",
      identificationNumber: "",
      dic: "",
      isCzechSoleProprietor: false,
      isVatPayer: null,
      email: "",
    },
    signature: {},
    vatEnabled: false,
    vatRate: 21,
    customTaxes: [],
    status: undefined as any,
  };

  // --- 5. Main draft state
  const [draft, originalSetDraft] = useState<Draft>(() => {
    const saved = localStorage.getItem(LS_DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Draft;
        // Handle migration from old format
        if (!parsed.paymentInfo) parsed.paymentInfo = emptyDraft.paymentInfo;
        if (!parsed.paymentLink) parsed.paymentLink = emptyDraft.paymentLink;
        if (!parsed.paymentOptions)
          parsed.paymentOptions = emptyDraft.paymentOptions;
        if (!parsed.sellerInfo) parsed.sellerInfo = emptyDraft.sellerInfo;
        if (!parsed.signature) parsed.signature = emptyDraft.signature;
        if (!parsed.status) parsed.status = emptyDraft.status;
        if (!parsed.customTaxes) parsed.customTaxes = emptyDraft.customTaxes;

        // Handle address migration
        if (parsed.sellerInfo && !parsed.sellerInfo.address) {
          const oldSellerInfo = parsed.sellerInfo as any;
          if (oldSellerInfo.addr1 || oldSellerInfo.addr2) {
            parsed.sellerInfo.address = [
              oldSellerInfo.addr1,
              oldSellerInfo.addr2,
            ]
              .filter(Boolean)
              .join("\n");
          }
        }

        // Migrate old legal fields to notes
        if (parsed.sellerInfo && !parsed.sellerInfo.notes) {
          const oldSellerInfo = parsed.sellerInfo as any; // Type assertion for migration
          if (oldSellerInfo.legal1) {
            parsed.sellerInfo.notes = `${oldSellerInfo.legal1}\n${
              oldSellerInfo.legal2 || ""
            }: ${parsed.sellerInfo.identificationNumber}`;
          }
        }
        // Don't set temporary number here - let loadExistingInvoice handle it
        return parsed;
      } catch {}
    }
    // For new invoice, start with empty invoiceNo - will be generated by loadExistingInvoice
    return {
      ...emptyDraft,
      invoiceNo: "", // Empty - will be generated by loadExistingInvoice via API
    };
  });

  // --- 6. UI and Data states
  const [activeTab, setActiveTab] = useState("customer");

  // ARES states
  const [aresLoading, setAresLoading] = useState(false);
  const [aresStatus, setAresStatus] = useState<
    "idle" | "success" | "not_found" | "error"
  >("idle");
  const [isSaving, setIsSaving] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(LS_ONBOARDING_COMPLETED);
    const isNewRegistration =
      sessionStorage.getItem("new_registration") === "true";

    if (!completed || isNewRegistration) {
      setShowOnboarding(true);
      if (isNewRegistration) {
        sessionStorage.removeItem("new_registration");
      }
    }
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem(LS_ONBOARDING_COMPLETED, "true");
    setShowOnboarding(false);
  };

  const startTour = () => {
    localStorage.removeItem(LS_ONBOARDING_COMPLETED);
    setShowOnboarding(true);
  };

  const [isSaved, setIsSaved] = useState(true);
  const isLoadingInvoiceRef = useRef(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showCzechSoleProprietorTooltip, setShowCzechSoleProprietorTooltip] =
    useState(false);
  const [showDueDateSettings, setShowDueDateSettings] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showItemsDialog, setShowItemsDialog] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [showInvoiceTemplateModal, setShowInvoiceTemplateModal] =
    useState(false);
  const [showValidationConfirmModal, setShowValidationConfirmModal] =
    useState(false);
  const [showSaveValidationModal, setShowSaveValidationModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsDialogTab, setSettingsDialogTab] = useState<
    "general" | "seller"
  >("general");
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePassword, setSharePassword] = useState("");
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [generatedShareUrl, setShareUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && !!navigator.share) {
      setCanShare(true);
    }
  }, []);

  const handleGenerateShare = async () => {
    if (!invoiceInternalId || !sharePassword) return;
    setIsGeneratingShare(true);
    try {
      const response = await authenticatedFetch(
        `/api/invoices/${invoiceInternalId}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            password: sharePassword,
            expiresDays: 30, // Default share expiration days
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}${data.url}`;
        setShareUrl(fullUrl);
      } else {
        alert(
          locale === "ru"
            ? "Ошибка при создании ссылки"
            : locale === "cs"
              ? "Chyba při vytváření odkazu"
              : "Error creating share link",
        );
      }
    } catch (error) {
      console.error("Error creating share link:", error);
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const [clients, setClients] = useState<Client[]>([]);
  const [savedItems, setSavedItems] = useState<Item[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<
    SavedPaymentMethod[]
  >([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<
    string | null
  >(null);
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>({});
  const [designSettings, setDesignSettings] = useState({
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    template: "Стандартный",
    accentColor: "#2563eb",
    textColor: "#000000",
    backgroundColor: "#ffffff",
  });
  const [customPresets, setCustomPresets] = useState<
    Array<{ name: string; value: string }>
  >([]);
  const [, forceUpdate] = useState({});

  const setDraft = (value: React.SetStateAction<Draft>) => {
    // Override setDraft to add sanitization for string fields in top-level updates
    if (typeof value === "function") {
      originalSetDraft((prev) => {
        const newValue = value(prev);
        // We could sanitize here but it's complex to check changed fields
        return newValue;
      });
    } else {
      originalSetDraft(value);
    }
  };

  const setField = React.useCallback(
    (k: keyof Draft, v: any) => setDraft((d) => ({ ...d, [k]: v })),
    [],
  );

  const setSellerField = (k: keyof SellerInfo, v: any) =>
    setDraft((d) => ({ ...d, sellerInfo: { ...d.sellerInfo, [k]: v } }));

  const addItem = () =>
    setDraft((d) => ({
      ...d,
      items: [
        ...d.items,
        {
          id: crypto.randomUUID(),
          description: "",
          quantity: 1,
          amount: 0,
          unit: "",
        },
      ],
    }));

  const removeItem = (id: string) => {
    setDraft((d) => ({
      ...d,
      items: d.items.filter((item) => item.id !== id),
    }));
    // Clean up the amount input state for removed item
    setAmountInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[id];
      return newInputs;
    });
  };

  const updateItem = (id: string, patch: Partial<Item>) =>
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));

  // Force re-render when invoice language changes
  useEffect(() => {
    forceUpdate({});
  }, [invoiceLanguage]);

  // Save function with backend logic simulation
  const saveInvoice = async (options: { silent?: boolean } = {}) => {
    // Check validation but save anyway
    const errors = getValidationErrors();
    if (errors.length > 0 && !options.silent) {
      setShowSaveValidationModal(true);
    }

    setIsSaving(true);
    try {
      // Save to backend (this is the only place where invoice data should be stored)
      const result = await saveInvoiceJson();

      // Update internalId if it was generated by backend
      if (result?.internalId && result.internalId !== invoiceInternalId) {
        setInvoiceInternalId(result.internalId);
        localStorage.setItem("invoice-internal-id", result.internalId);
      }

      // Mark as saved
      setIsSaved(true);

      // If this invoice was created from time tracking, mark tasks as billed
      if (
        (draft as any).timeTrackingTaskIds &&
        (draft as any).timeTrackingTaskIds.length > 0
      ) {
        try {
          const markBilledResp = await authenticatedFetch(
            "/api/time-tracking/tasks/mark-billed",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                taskIds: (draft as any).timeTrackingTaskIds,
              }),
            },
          );

          if (markBilledResp.ok) {
            // Remove the IDs from draft so we don't mark them again on next save
            setDraft((d) => {
              const newDraft = { ...d };
              delete (newDraft as any).timeTrackingTaskIds;
              delete (newDraft as any).isFromTimeTracking;
              return newDraft;
            });
          }
        } catch (err) {
          console.error("Error marking time tracking tasks as billed:", err);
        }
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      alert(
        "Ошибка сохранения счёта-фактуры: " +
          (error instanceof Error ? error.message : "Unknown error"),
      );
      setIsSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Update payment option titles when language changes
  useEffect(() => {
    if (draft.paymentOptions && draft.paymentOptions.length > 0) {
      setDraft((d) => ({
        ...d,
        paymentOptions: d.paymentOptions?.map((option) => {
          // Update title based on type, not current text
          // This ensures titles update when invoice language changes
          let newTitle = option.title;
          if (option.type === "bank") {
            newTitle = currentInvoiceT.bankTransfer;
          } else if (option.type === "link") {
            newTitle = currentInvoiceT.paymentLink;
          } else if (option.type === "cash") {
            newTitle = currentInvoiceT.cash;
          } else if (option.type === "custom") {
            // For custom, only update if it matches one of the standard translations
            const allTranslations = Object.values(invoiceTranslations);
            const isStandardTitle = allTranslations.some(
              (t) =>
                option.title === t.bankTransfer ||
                option.title === t.paymentLink ||
                option.title === t.cash ||
                option.title === t.custom,
            );
            if (isStandardTitle) {
              newTitle = currentInvoiceT.custom;
            }
            // Otherwise keep custom title as is
          }
          return {
            ...option,
            title: newTitle,
          };
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceLanguage]);

  // Internal ID for invoice tracking
  // --- Validation state
  const getValidationErrors = () => {
    const errors: string[] = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // 1. Basics
    if (!draft.invoiceNo?.trim()) errors.push("invoiceNo");
    if (!draft.issueDate?.trim()) errors.push("issueDate");
    if (!draft.dueDate?.trim()) errors.push("dueDate");

    // 2. Seller
    if (!draft.sellerInfo?.name?.trim()) errors.push("sellerName");
    if (!draft.sellerInfo?.identificationNumber?.trim())
      errors.push("sellerTaxId");
    if (!draft.sellerInfo?.address?.trim()) errors.push("sellerAddress");
    if (draft.sellerInfo?.isVatPayer && !draft.sellerInfo?.dic?.trim()) {
      errors.push("sellerVatNumber");
    }
    if (
      draft.sellerInfo?.email?.trim() &&
      !emailRegex.test(draft.sellerInfo.email.trim())
    ) {
      errors.push("sellerEmail");
    }

    // 3. Client (Buyer)
    // Rule: If all client fields are empty, it's valid (anonymous).
    // If ANY field is filled, name, taxId, and address become required.
    const isBuyerEmpty =
      !draft.buyerName?.trim() &&
      !draft.buyerAddress?.trim() &&
      !draft.buyerId?.trim() &&
      !draft.buyerTaxId?.trim() &&
      !draft.buyerEmail?.trim() &&
      !draft.buyerPhone?.trim();

    if (!isBuyerEmpty) {
      if (!draft.buyerName?.trim()) errors.push("buyerName");
      if (!draft.buyerId?.trim()) errors.push("buyerTaxId");
      if (!draft.buyerAddress?.trim()) errors.push("buyerAddress");
      if (draft.buyerIsVatPayer && !draft.buyerTaxId?.trim()) {
        errors.push("buyerVatNumber");
      }
      if (
        draft.buyerEmail?.trim() &&
        !emailRegex.test(draft.buyerEmail.trim())
      ) {
        errors.push("buyerEmail");
      }
    }

    // 4. Items
    if (!draft.items || draft.items.length === 0) {
      errors.push("items");
    } else {
      const hasIncompleteItems = draft.items.some(
        (item) => !item.description?.trim() || !item.amount || item.amount <= 0,
      );
      if (hasIncompleteItems) {
        errors.push("itemsValid");
      }
    }

    // 5. Payment Methods
    if (!draft.paymentOptions || draft.paymentOptions.length === 0) {
      errors.push("paymentOptions");
    } else {
      draft.paymentOptions.forEach((po) => {
        if (po.type === "bank") {
          if (!po.accountNumber?.trim() && !po.iban?.trim()) {
            errors.push(`paymentBank_${po.id}`);
          }
        } else if (po.type === "link") {
          if (!po.paymentUrl?.trim()) {
            errors.push(`paymentLink_${po.id}`);
          }
        } else if (po.type === "custom") {
          if (!po.details?.trim()) {
            errors.push(`paymentCustom_${po.id}`);
          }
        }
      });
    }

    return errors;
  };

  // Reset field errors when invoice internal ID changes (e.g. creating new invoice)
  useEffect(() => {
    setShowFieldErrors(false);
  }, [invoiceInternalId]);

  const validationMessage = useMemo(() => {
    const allErrors = getValidationErrors();
    if (allErrors.length === 0) return null;

    const fieldsMap = t.invoice.validation.fields as Record<string, string>;
    const warningKeys = ["buyerTaxId", "buyerAddress"];

    const errors = allErrors.filter((k) => !warningKeys.includes(k));
    const warnings = allErrors.filter((k) => warningKeys.includes(k));

    const renderList = (keys: string[], colorClass: string) => {
      if (keys.length === 0) return null;
      const uniqueNames = keys
        .map((key) => {
          if (key.startsWith("paymentBank_"))
            return { name: fieldsMap.paymentBank, key };
          if (key.startsWith("paymentLink_"))
            return { name: fieldsMap.paymentLink, key };
          if (key.startsWith("paymentCustom_"))
            return { name: fieldsMap.paymentCustom, key };
          return { name: fieldsMap[key] || key, key };
        })
        .filter((v, i, a) => a.findIndex((t) => t.name === v.name) === i);

      return (
        <span className="inline-flex flex-wrap gap-x-1">
          {uniqueNames.map((field, index) => (
            <React.Fragment key={field.key}>
              <span className={`${colorClass} font-bold`}>{field.name}</span>
              {index < uniqueNames.length - 1 ? ", " : ""}
            </React.Fragment>
          ))}
        </span>
      );
    };

    const errorList = renderList(errors, "text-red-500");
    const warningList = renderList(warnings, "text-amber-500");

    const getMessageParts = (isDownload: boolean) => {
      const template = isDownload
        ? t.invoice.validation.modalMessageDownload
        : t.invoice.validation.modalMessage;
      return template.split("{fields}");
    };

    // Logic: if there are ANY warnings, hide the red summary text in the modal
    // and only show the yellow warning box.
    const renderErrorList = (isDownload: boolean) => {
      if (errors.length === 0 || warnings.length > 0) return null;
      const parts = getMessageParts(isDownload);
      return (
        <div className="mb-4 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
          <span>{parts[0]}</span>
          {errorList}
          <span>{parts[1]}</span>
        </div>
      );
    };

    const renderWarningSummary = (isDownload: boolean) => {
      if (errors.length > 0 || warnings.length === 0) return null;
      const template = isDownload
        ? t.invoice.validation.modalMessageDownload
        : t.invoice.validation.modalMessage;
      return (
        <div className="mb-4 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
          {template
            .replace("{fields}", "")
            .replace(/[,:.\s]+$/, "")
            .trim()}
          ?
        </div>
      );
    };

    return {
      render: (isDownload: boolean) => (
        <>
          {renderErrorList(isDownload)}
          {renderWarningSummary(isDownload)}
          {warnings.length > 0 && (
            <div className="p-3 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/50 rounded-2xl">
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 mb-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="text-[10px] font-black uppercase tracking-wider">
                  Warning
                </span>
              </div>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 font-bold leading-tight mb-2">
                {t.invoice.validation.errorRequiredWarning}
              </p>
              <div className="text-xs">{warningList}</div>
            </div>
          )}
        </>
      ),
    };
  }, [t, draft]);

  const basicsErrorMessage = useMemo(() => {
    if (!showFieldErrors) return null;
    const sectionFields = ["invoiceNo", "issueDate", "dueDate"];
    const errors = getValidationErrors().filter((e) =>
      sectionFields.includes(e),
    );
    if (errors.length === 0) return null;
    const fieldsMap = t.invoice.validation.fields as Record<string, string>;
    const fieldsList = errors.map((key) => fieldsMap[key] || key).join(", ");
    return `${t.invoice.validation.sectionError}: ${fieldsList}`;
  }, [t, draft, showFieldErrors]);

  const partiesErrorMessage = useMemo(() => {
    if (!showFieldErrors) return null;
    const sellerFields = [
      "sellerName",
      "sellerTaxId",
      "sellerAddress",
      "sellerVatNumber",
      "sellerEmail",
    ];
    const buyerFields = [
      "buyerName",
      "buyerTaxId",
      "buyerAddress",
      "buyerVatNumber",
      "buyerEmail",
    ];
    const sectionFields = [...sellerFields, ...buyerFields];

    const allErrors = getValidationErrors().filter((e) =>
      sectionFields.includes(e),
    );
    if (allErrors.length === 0) return null;

    const fieldsMap = t.invoice.validation.fields as Record<string, string>;
    const warningKeys = ["buyerTaxId", "buyerAddress"];

    const errors = allErrors.filter((k) => !warningKeys.includes(k));
    const warnings = allErrors.filter((k) => warningKeys.includes(k));

    // Logic: if there are ANY warnings, hide the red summary box for this section
    // and only show the yellow warning box.
    return {
      hasCustomerErrors: allErrors.some(
        (e) => buyerFields.includes(e) && !warningKeys.includes(e),
      ),
      hasSellerErrors: allErrors.some((e) => sellerFields.includes(e)),
      errors:
        errors.length > 0 && warnings.length === 0 ? (
          <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>
              {t.invoice.validation.sectionError}:{" "}
              {errors.map((key, index) => (
                <React.Fragment key={key}>
                  <span className="text-red-500">{fieldsMap[key] || key}</span>
                  {index < errors.length - 1 ? ", " : ""}
                </React.Fragment>
              ))}
            </span>
          </div>
        ) : null,
      warnings:
        warnings.length > 0 ? (
          <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>
              {t.invoice.validation.errorRequiredWarning}:{" "}
              {warnings.map((key, index) => (
                <React.Fragment key={key}>
                  <span className="text-amber-500">
                    {fieldsMap[key] || key}
                  </span>
                  {index < warnings.length - 1 ? ", " : ""}
                </React.Fragment>
              ))}
            </span>
          </div>
        ) : null,
    };
  }, [t, draft, showFieldErrors]);

  const itemsErrorMessage = useMemo(() => {
    if (!showFieldErrors) return null;
    const sectionFields = ["items", "itemsValid"];
    const errors = getValidationErrors().filter((e) =>
      sectionFields.includes(e),
    );
    if (errors.length === 0) return null;
    const fieldsMap = t.invoice.validation.fields as Record<string, string>;
    const fieldsList = errors.map((key) => fieldsMap[key] || key).join(", ");
    return `${t.invoice.validation.sectionError}: ${fieldsList}`;
  }, [t, draft, showFieldErrors]);

  const paymentErrorMessage = useMemo(() => {
    if (!showFieldErrors) return null;
    const errors = getValidationErrors().filter(
      (e) =>
        e === "paymentOptions" ||
        e.startsWith("paymentBank_") ||
        e.startsWith("paymentLink_") ||
        e.startsWith("paymentCustom_"),
    );
    if (errors.length === 0) return null;

    const fieldsMap = t.invoice.validation.fields as Record<string, string>;
    const fieldsList = errors
      .map((key) => {
        if (key.startsWith("paymentBank_")) return fieldsMap.paymentBank;
        if (key.startsWith("paymentLink_")) return fieldsMap.paymentLink;
        if (key.startsWith("paymentCustom_")) return fieldsMap.paymentCustom;
        return fieldsMap[key] || key;
      })
      .filter((value, index, self) => self.indexOf(value) === index)
      .join(", ");

    return `${t.invoice.validation.sectionError}: ${fieldsList}`;
  }, [t, draft, showFieldErrors]);

  const getLabelClass = (
    field: string,
    baseClass: string = "text-sm font-medium",
  ) => {
    const hasError = showFieldErrors && getValidationErrors().includes(field);
    if (!hasError) return `${baseClass} text-slate-700 dark:text-slate-300`;

    const isWarning = field === "buyerTaxId" || field === "buyerAddress";
    if (isWarning) {
      return `${baseClass} text-amber-600 dark:text-amber-400`;
    }

    return `${baseClass} text-red-600 dark:text-red-400`;
  };

  const getInputClass = (field: string, baseClass: string) => {
    const hasError = showFieldErrors && getValidationErrors().includes(field);
    const isWarning = field === "buyerTaxId" || field === "buyerAddress";

    if (!hasError) return baseClass;

    if (isWarning) {
      return `${baseClass} border-amber-500 focus:ring-amber-500 bg-amber-50 dark:bg-amber-900/10`;
    }

    return `${baseClass} border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/10`;
  };

  const [invoiceTemplate, setInvoiceTemplate] = useState<string>("");
  const [, setInvoiceTemplateScope] = useState<string>("global");

  // --- Invoice JSON management functions
  const saveInvoiceJson = async () => {
    try {
      // Validate required fields before saving
      if (!draft || !designSettings || !invoiceLanguage) {
        throw new Error(
          "Missing required fields: draft, designSettings, or language",
        );
      }

      // Получаем глобальный дефолтный шаблон с бэкенда как Fallback.
      // Это гарантирует корректность даже при отсутствии выбранного шаблона в UI.
      const getDefaultTemplate = async (): Promise<string> => {
        try {
          const r = await authenticatedFetch("/api/settings", {
            ttl: 60000, // Cache for 1 minute
            cacheKey: "user-settings",
          });
          if (r.ok) {
            const data = await r.json();
            return data.invoiceNumberTemplate || "YYMMDDNN";
          }
        } catch {}
        return "YYMMDDNN";
      };

      // Определяем итоговый шаблон
      const tpl =
        invoiceTemplate && invoiceTemplate.trim().length > 0
          ? invoiceTemplate
          : await getDefaultTemplate();

      // Проверяем, является ли текущий номер кастомным (не соответствует шаблону)
      // Номер считается кастомным, если он был изменен вручную пользователем
      // Простая проверка: если номер не пустой и не соответствует шаблону с текущими параметрами, считаем кастомным
      const isCustomNumber = (invoiceNo: string, template: string): boolean => {
        if (!invoiceNo || invoiceNo.trim().length === 0) return false;

        // Если шаблон не содержит seq, проверяем только по дате и customer
        if (!/\{?seq(?::\d+)?\}?|NN/.test(template)) {
          try {
            const generatedNo = renderInvoiceFromTemplate(template, {
              date: draft.issueDate,
              customer: draft.buyerName,
              seq: 1,
            });
            return invoiceNo !== generatedNo;
          } catch {
            return true;
          }
        }

        // Если шаблон содержит seq, проверяем несколько значений seq (оптимизированно)
        try {
          // Проверяем только первые 100 значений seq (достаточно для большинства случаев)
          // Если номер не совпадает ни с одним, считаем кастомным
          for (let seq = 1; seq <= 100; seq++) {
            const generatedNo = renderInvoiceFromTemplate(template, {
              date: draft.issueDate,
              customer: draft.buyerName,
              seq,
            });
            if (invoiceNo === generatedNo) {
              return false; // Номер соответствует шаблону
            }
          }
          // Если не совпал ни с одним, считаем кастомным
          return true;
        } catch {
          // Если ошибка при генерации, считаем номер кастомным (на всякий случай)
          return true;
        }
      };

      let finalInvoiceNo = draft.invoiceNo;

      // Если номер кастомный (не соответствует шаблону), сохраняем его как есть
      // Только если номер пустой или соответствует шаблону, генерируем новый
      if (
        !draft.invoiceNo ||
        draft.invoiceNo.trim().length === 0 ||
        !isCustomNumber(draft.invoiceNo, tpl)
      ) {
        // Если шаблон содержит seq — инкрементим счётчик и рендерим номер
        if (/\{?seq(?::\d+)?\}?|NN/.test(tpl)) {
          const resp = await authenticatedFetch(
            "/api/invoice-templates/generate",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ template: tpl }),
            },
          );
          if (resp.ok) {
            const data = await resp.json();
            finalInvoiceNo = renderInvoiceFromTemplate(tpl, {
              date: draft.issueDate,
              customer: draft.buyerName,
              seq: data.counter || 1,
            });
          } else {
            finalInvoiceNo = renderInvoiceFromTemplate(tpl, {
              date: draft.issueDate,
              customer: draft.buyerName,
              seq: 1,
            });
          }
        } else {
          // Без seq — просто рендерим по дате и значениям
          finalInvoiceNo = renderInvoiceFromTemplate(tpl, {
            date: draft.issueDate,
            customer: draft.buyerName,
            seq: 1,
          });
        }
      }
      // Если номер кастомный, оставляем его как есть (finalInvoiceNo уже равен draft.invoiceNo)

      // Filter out empty payment options before saving
      const filteredPaymentOptions = (draft.paymentOptions || []).filter(
        (po) => !isPaymentOptionEmpty(po),
      );

      const payloadDraft = {
        ...draft,
        invoiceNo: finalInvoiceNo,
        numberTemplate: tpl,
        paymentOptions: filteredPaymentOptions,
      };

      const response = await authenticatedFetch("/api/invoices/save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          internalId: invoiceInternalId,
          draft: payloadDraft,
          designSettings,
          language: invoiceLanguage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Update internalId from response if provided
      if (result.internalId) {
        setInvoiceInternalId(result.internalId);
        localStorage.setItem("invoice-internal-id", result.internalId);
      }

      return result;
    } catch (error) {
      console.error("Error saving invoice JSON:", error);
      throw error;
    }
  };

  const updateDownloadCount = async () => {
    try {
      // Больше не инкрементируем на скачивании — счётчик отражает сохранённые фактуры
      const response = await authenticatedFetch(
        `/api/invoices/${invoiceInternalId}/download`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error updating download count:", error);
      // Don't throw error here as it shouldn't block PDF download
    }
  };

  // --- ARES company lookup
  const handleAresLookup = async (isBuyer = true) => {
    const ico = isBuyer
      ? draft.buyerId || ""
      : draft.sellerInfo.identificationNumber || "";
    if (!isValidIco(ico)) {
      setAresStatus("error");
      return;
    }

    setAresLoading(true);
    setAresStatus("idle");

    try {
      const response = await authenticatedFetch(`/api/utils/ares/${ico}`);
      if (response.status === 404) {
        setAresStatus("not_found");
        return;
      }
      if (!response.ok) {
        throw new Error("ARES lookup failed");
      }

      const data = await response.json();

      if (isBuyer) {
        setDraft((prev) => ({
          ...prev,
          buyerName: data.obchodniJmeno || prev.buyerName,
          buyerAddress: data.sidlo?.textovaAdresa || prev.buyerAddress,
          buyerTaxId: data.dic || prev.buyerTaxId,
        }));
      } else {
        setDraft((prev) => ({
          ...prev,
          sellerInfo: {
            ...prev.sellerInfo,
            name: data.obchodniJmeno || prev.sellerInfo.name,
            address: data.sidlo?.textovaAdresa || prev.sellerInfo.address,
            dic: data.dic || prev.sellerInfo.dic,
          },
        }));
      }

      setAresStatus("success");
    } catch (error) {
      console.error("ARES lookup error:", error);
      setAresStatus("error");
    } finally {
      setAresLoading(false);
    }
  };

  // --- Client management functions
  const saveClient = async (client: Omit<Client, "id">) => {
    try {
      const response = await authenticatedFetch("/api/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(client),
      });
      if (!response.ok) throw new Error("Failed to save client");
      const newClient = await response.json();
      setClients((prev) => [...prev, newClient]);
      return newClient;
    } catch (error) {
      console.error("Error saving client:", error);
      alert("Failed to save client");
      throw error;
    }
  };

  const updateClient = async (id: string, client: Partial<Client>) => {
    try {
      const response = await authenticatedFetch(`/api/clients/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(client),
      });
      if (!response.ok) throw new Error("Failed to update client");
      const updatedClient = await response.json();
      setClients((prev) => prev.map((c) => (c.id === id ? updatedClient : c)));
      return updatedClient;
    } catch (error) {
      console.error("Error updating client:", error);
      alert("Failed to update client");
      throw error;
    }
  };

  const deleteClient = async (id: string) => {
    try {
      const response = await authenticatedFetch(`/api/clients/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete client");
      setClients((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("Failed to delete client");
      throw error;
    }
  };

  // --- Payment methods management functions
  const savePaymentMethod = async (method: Omit<SavedPaymentMethod, "id">) => {
    try {
      // Clean the method object: remove undefined values and fields not in database schema
      // Database fields: type, title, details, accountNumber, iban, bic, bankName, bankAddress, variableSymbol, constantSymbol, specificSymbol
      const cleanedMethod: Record<string, any> = {
        type: method.type,
        title: method.title || "",
        details: method.details || null,
        accountNumber: method.accountNumber || null,
        iban: method.iban || null,
        bic: method.bic || null,
        bankName: method.bankName || null,
        bankAddress: method.bankAddress || null,
        variableSymbol: method.variableSymbol || null,
        constantSymbol: method.constantSymbol || null,
        specificSymbol: method.specificSymbol || null,
        paymentUrl: method.paymentUrl || null,
        showQRCode: method.showQRCode === true,
      };

      const response = await authenticatedFetch("/api/payment-methods", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedMethod),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Failed to save payment method:",
          response.status,
          errorData,
        );
        throw new Error(
          errorData.error ||
            `Failed to save payment method: ${response.status}`,
        );
      }

      const newMethod = await response.json();
      setSavedPaymentMethods((prev) => [...prev, newMethod]);
      return newMethod;
    } catch (error) {
      console.error("Error saving payment method:", error);
      alert(
        `Failed to save payment method: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      throw error;
    }
  };

  const updatePaymentMethod = async (
    id: string,
    method: Partial<SavedPaymentMethod>,
  ) => {
    try {
      // Clean the method object: remove undefined values and fields not in database schema
      const cleanedMethod: Record<string, any> = {};
      if (method.type !== undefined) cleanedMethod.type = method.type;
      if (method.title !== undefined) cleanedMethod.title = method.title || "";
      if (method.details !== undefined)
        cleanedMethod.details = method.details || null;
      if (method.accountNumber !== undefined)
        cleanedMethod.accountNumber = method.accountNumber || null;
      if (method.iban !== undefined) cleanedMethod.iban = method.iban || null;
      if (method.bic !== undefined) cleanedMethod.bic = method.bic || null;
      if (method.bankName !== undefined)
        cleanedMethod.bankName = method.bankName || null;
      if (method.bankAddress !== undefined)
        cleanedMethod.bankAddress = method.bankAddress || null;
      if (method.variableSymbol !== undefined)
        cleanedMethod.variableSymbol = method.variableSymbol || null;
      if (method.constantSymbol !== undefined)
        cleanedMethod.constantSymbol = method.constantSymbol || null;
      if (method.specificSymbol !== undefined)
        cleanedMethod.specificSymbol = method.specificSymbol || null;
      if (method.paymentUrl !== undefined)
        cleanedMethod.paymentUrl = method.paymentUrl || null;
      if (method.showQRCode !== undefined)
        cleanedMethod.showQRCode = method.showQRCode;

      const response = await authenticatedFetch(`/api/payment-methods/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedMethod),
      });
      if (!response.ok) throw new Error("Failed to update payment method");
      const updatedMethod = await response.json();
      setSavedPaymentMethods((prev) =>
        prev.map((m) => (m.id === id ? updatedMethod : m)),
      );
      return updatedMethod;
    } catch (error) {
      console.error("Error updating payment method:", error);
      alert("Failed to update payment method");
      throw error;
    }
  };

  const deletePaymentMethod = async (id: string) => {
    try {
      const response = await authenticatedFetch(`/api/payment-methods/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete payment method");
      setSavedPaymentMethods((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error("Error deleting payment method:", error);
      alert("Failed to delete payment method");
      throw error;
    }
  };

  // Check if payment option is empty (only title filled or only IBAN/accountNumber)
  // A payment option is considered empty if only the title is filled, or only IBAN, or only accountNumber
  // At least one field besides title must be filled for the option to be valid
  const isPaymentOptionEmpty = (po: PaymentOption): boolean => {
    if (po.type === "bank") {
      // For bank: check if only title is filled, or only IBAN, or only accountNumber
      const hasIban = !!po.iban?.trim();
      const hasAccountNumber = !!po.accountNumber?.trim();
      const hasBic = !!po.bic?.trim();
      const hasBankName = !!po.bankName?.trim();
      const hasBankAddress = !!po.bankAddress?.trim();
      const hasVariableSymbol = !!po.variableSymbol?.trim();
      const hasConstantSymbol = !!po.constantSymbol?.trim();
      const hasSpecificSymbol = !!po.specificSymbol?.trim();

      // Count filled fields (excluding title)
      const filledFields = [
        hasIban,
        hasAccountNumber,
        hasBic,
        hasBankName,
        hasBankAddress,
        hasVariableSymbol,
        hasConstantSymbol,
        hasSpecificSymbol,
      ].filter(Boolean).length;

      // Empty if no fields filled (excluding title)
      return filledFields === 0;
    } else if (po.type === "link") {
      // For link: empty if no paymentUrl
      return !po.paymentUrl?.trim();
    } else {
      // cash or custom: empty if no details
      return !po.details?.trim();
    }
  };

  const addPaymentMethodFromSaved = (savedMethodId: string) => {
    const savedMethod = savedPaymentMethods.find((m) => m.id === savedMethodId);
    if (savedMethod) {
      setDraft((d) => {
        const currentOptions = d.paymentOptions || [];

        // Check if there's exactly one payment option and it's empty
        if (currentOptions.length === 1) {
          const existingOption = currentOptions[0];
          // Check if existing option is empty (only title or only IBAN/accountNumber)
          // Also check if types match (or existing is bank with only IBAN/accountNumber)
          if (isPaymentOptionEmpty(existingOption)) {
            // Replace the empty option with the saved method if types match
            // Or if existing is bank type (can be replaced with any type)
            if (
              existingOption.type === savedMethod.type ||
              existingOption.type === "bank"
            ) {
              return {
                ...d,
                paymentOptions: [
                  {
                    ...savedMethod,
                    id: existingOption.id, // Keep the same ID
                  },
                ],
              };
            }
          }
        }

        // Otherwise, add as new option
        const newMethod: PaymentOption = {
          ...savedMethod,
          id: crypto.randomUUID(), // Generate new ID for the draft
        };
        return {
          ...d,
          paymentOptions: [...currentOptions, newMethod],
        };
      });
    }
  };

  const saveCurrentPaymentMethodAsTemplate = (paymentOption: PaymentOption) => {
    // Create a copy without the draft ID
    const { id, ...methodData } = paymentOption;

    // Generate new ID for saved template
    const newTemplate: SavedPaymentMethod = {
      ...methodData,
      id: crypto.randomUUID(),
    };

    setSavedPaymentMethods((prev) => [...prev, newTemplate]);

    // Show confirmation
    alert(`✅ Payment method "${paymentOption.title}" saved as template!`);
  };

  const loadSellerDefaults = async () => {
    try {
      const r = await authenticatedFetch("/api/settings");
      if (!r.ok) return;
      const data = await r.json();

      setIsEmailConfigured(
        !!(data.notificationEmailHandle && data.notificationEmailName),
      );
      setEmailConfigLoaded(true);

      const defaultSellerInfo = {
        name: data.sellerName || "",
        address: data.sellerAddress || "",
        identificationNumber: data.sellerTaxId || "",
        dic: data.sellerDic || "",
        website: data.sellerWebsite || "",
        email: data.sellerEmail || "",
        isVatPayer:
          data.sellerIsVatPayer !== undefined && data.sellerIsVatPayer !== null
            ? data.sellerIsVatPayer
            : undefined,
        isCzechSoleProprietor: data.sellerIsCzechSoleProprietor || false,
        notes: data.sellerNotes || "",
        country: data.sellerCountry || "",
      };

      setDraft((d) => {
        const updatedDraft = {
          ...d,
          sellerInfo: {
            ...d.sellerInfo,
            ...defaultSellerInfo,
          },
          vatEnabled:
            data.sellerIsVatPayer !== undefined &&
            data.sellerIsVatPayer !== null
              ? data.sellerIsVatPayer
              : d.vatEnabled,
        };

        // Auto-fill default payment method if specified and no methods exist yet
        if (
          data.defaultPaymentMethodId &&
          (!d.paymentOptions || d.paymentOptions.length === 0)
        ) {
          // Note: we need the full method data, but we only have the ID.
          // Since savedPaymentMethods might not be loaded yet, we'll handle this
          // in a separate useEffect or after loading methods.
        }

        return updatedDraft;
      });

      // If default payment method is set, try to apply it
      if (data.defaultPaymentMethodId) {
        setDefaultPaymentMethodId(data.defaultPaymentMethodId);
      }
    } catch (error) {
      console.error("Error loading seller defaults:", error);
    }
  };

  const openSellerSettings = () => {
    setSettingsDialogTab("seller");
    setShowSettingsModal(true);
  };

  useEffect(() => {
    loadSellerDefaults();
  }, []);

  // Load existing invoice from backend if internalId is present
  useEffect(() => {
    const loadExistingInvoice = async () => {
      const savedInternalId = localStorage.getItem("invoice-internal-id");
      const skipInvoiceCheck = localStorage.getItem("skip-invoice-check");

      // Если установлен флаг skip-invoice-check, очищаем его и не делаем запрос
      if (skipInvoiceCheck === "true") {
        localStorage.removeItem("skip-invoice-check");
        if (savedInternalId) {
          localStorage.removeItem("invoice-internal-id");
        }
        setInvoiceInternalId("");
        // Продолжаем выполнение - создадим новый инвойс
      } else if (savedInternalId && savedInternalId !== invoiceInternalId) {
        setInvoiceInternalId(savedInternalId);
        return; // Wait for state update before continuing
      }

      // ========== СЛУЧАЙ 1: ЗАГРУЗКА СУЩЕСТВУЮЩЕГО ИНВОЙСА ==========
      // Если есть internalId и он начинается с "inv_", это существующий инвойс
      if (invoiceInternalId && invoiceInternalId.startsWith("inv_")) {
        try {
          const response = await authenticatedFetch(
            `/api/invoices/${invoiceInternalId}`,
          );
          if (response.ok) {
            const invoiceData = await response.json();
            const activeVersion = invoiceData.versions?.find(
              (v: any) => v.metadata.isActive,
            );
            if (activeVersion) {
              // Загружаем данные существующего инвойса из БД
              isLoadingInvoiceRef.current = true;

              // Ensure all required fields exist in the draft (migration/safeguard)
              const backendDraft = activeVersion.draft || {};
              const safeDraft: Draft = {
                ...emptyDraft,
                ...backendDraft,
                invoiceNo: backendDraft.invoiceNo || "",
                paymentOptions: backendDraft.paymentOptions || [],
                customTaxes: backendDraft.customTaxes || [],
                items: backendDraft.items || [],
              };

              setDraft(safeDraft);
              setDesignSettings(activeVersion.designSettings || designSettings);
              setInvoiceLanguage(activeVersion.language || invoiceLanguage);

              // Загружаем шаблон если есть
              if (activeVersion.draft.numberTemplate) {
                setInvoiceTemplate(activeVersion.draft.numberTemplate);
                const tpl = activeVersion.draft.numberTemplate;
                const scope = tpl.includes("{customer}")
                  ? "customer"
                  : tpl.includes("{YYYY}") || tpl.includes("{YY}")
                    ? "month"
                    : "global";
                setInvoiceTemplateScope(scope);
              }

              return; // Выходим - не генерируем новый номер
            }
          } else if (response.status === 404) {
            // Инвойс не найден - очищаем internalId и создаем новый
            // Это ожидаемый случай при создании нового инвойса, не выводим ошибку
            setInvoiceInternalId("");
            localStorage.removeItem("invoice-internal-id");
            // Продолжаем выполнение - создадим новый инвойс
          } else {
            // Другие ошибки (не 404) - выводим предупреждение
            setInvoiceInternalId("");
            localStorage.removeItem("invoice-internal-id");
          }
        } catch (error) {
          // При ошибке сети тоже очищаем и создаем новый
          setInvoiceInternalId("");
          localStorage.removeItem("invoice-internal-id");
        }
      }

      // ========== СЛУЧАЙ 2: СОЗДАНИЕ НОВОГО ИНВОЙСА ==========
      // Если нет internalId или он не начинается с "inv_", это новый инвойс
      // Генерируем номер через API и показываем в инпуте
      if (!invoiceInternalId || !invoiceInternalId.startsWith("inv_")) {
        try {
          const r = await authenticatedFetch("/api/settings");
          if (!r.ok) return;
          const data = await r.json();
          const tpl: string = data.invoiceNumberTemplate || "YYMMDDNN";
          const scope = tpl.includes("{customer}")
            ? "customer"
            : tpl.includes("{YYYY}") || tpl.includes("{YY}")
              ? "month"
              : "global";
          setInvoiceTemplate(tpl);
          setInvoiceTemplateScope(scope);

          // Load default currency from backend
          const defaultCurrency: string =
            data.defaultCurrency || DEFAULT_CURRENCY;
          const currencyKey = Object.keys(currencyNames).find(
            (key) => currencyNames[key] === defaultCurrency,
          );

          if (data.defaultLanguage) {
            setInvoiceLanguage(data.defaultLanguage as LanguageCode);
          }

          // Prepare default seller info from settings
          const defaultSellerInfo = {
            name: data.sellerName || "",
            address: data.sellerAddress || "",
            identificationNumber: data.sellerTaxId || "",
            dic: data.sellerDic || "",
            website: data.sellerWebsite || "",
            isVatPayer:
              data.sellerIsVatPayer !== undefined &&
              data.sellerIsVatPayer !== null
                ? data.sellerIsVatPayer
                : undefined,
            isCzechSoleProprietor: data.sellerIsCzechSoleProprietor || false,
            notes: data.sellerNotes || "",
            country: data.sellerCountry || "",
          };

          const defaultVatEnabled =
            data.sellerIsVatPayer !== undefined &&
            data.sellerIsVatPayer !== null
              ? data.sellerIsVatPayer
              : false;

          // Генерируем номер для нового инвойса через API
          const date = new Date();
          let generatedNo = "";

          if (/\{?seq(?::\d+)?\}?|NN/.test(tpl)) {
            // Шаблон содержит seq - получаем счетчик с бэкенда
            try {
              const generateResp = await authenticatedFetch(
                "/api/invoice-templates/generate",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ template: tpl }),
                },
              );
              if (generateResp.ok) {
                const generateData = await generateResp.json();
                generatedNo = renderInvoiceFromTemplate(tpl, {
                  date,
                  customer: "",
                  seq: generateData.counter || 1,
                });
              } else {
                generatedNo = renderInvoiceFromTemplate(tpl, {
                  date,
                  customer: "",
                  seq: 1,
                });
              }
            } catch (error) {
              console.error("Error generating invoice number:", error);
              generatedNo = renderInvoiceFromTemplate(tpl, {
                date,
                customer: "",
                seq: 1,
              });
            }
          } else {
            // Шаблон без seq - генерируем сразу
            generatedNo = renderInvoiceFromTemplate(tpl, {
              date,
              customer: "",
              seq: 1,
            });
          }

          setDraft((d) => ({
            ...d,
            invoiceNo: generatedNo,
            currency: (d as any).isFromTimeTracking
              ? d.currency
              : defaultCurrency,
            currencyKey: (d as any).isFromTimeTracking
              ? d.currencyKey || ""
              : currencyKey || "",
            sellerInfo: {
              ...d.sellerInfo,
              ...defaultSellerInfo,
            },
            vatEnabled: defaultVatEnabled,
          }));
        } catch (error) {
          console.error("Error loading settings for new invoice:", error);
        }
      }
    };

    loadExistingInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceInternalId, location.pathname, location.key]);

  // Persist draft
  useEffect(() => {
    try {
      localStorage.setItem(LS_DRAFT_KEY, JSON.stringify(draft));
      if (isLoadingInvoiceRef.current) {
        setIsSaved(true);
        isLoadingInvoiceRef.current = false;
      } else {
        setIsSaved(false); // Mark as unsaved when draft changes
      }
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === "QuotaExceededError"
      ) {
        alert(
          "Storage limit exceeded. Please remove some images or clear browser data. Maximum image size: 200x100px, 70% quality.",
        );
        // Try to save without images
        const draftWithoutImages = {
          ...draft,
          sellerInfo: { ...draft.sellerInfo, logo: undefined },
          signature: {
            ...draft.signature,
            issuedSignature: undefined,
            acceptedSignature: undefined,
          },
        };
        try {
          localStorage.setItem(
            LS_DRAFT_KEY,
            JSON.stringify(draftWithoutImages),
          );
        } catch (e) {
          console.error("Failed to save even without images:", e);
        }
      } else {
        console.error("Failed to save to localStorage:", error);
      }
    }
  }, [draft]);

  // Persist due days setting
  useEffect(() => {
    localStorage.setItem(LS_DUE_DAYS_KEY, dueDays.toString());
  }, [dueDays]);

  // Persist invoice language setting
  useEffect(() => {
    localStorage.setItem(LS_LANGUAGE_KEY, invoiceLanguage);
  }, [invoiceLanguage]);

  // Update helpers
  // Load clients from API
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await authenticatedFetch("/api/clients");
        if (response.ok) {
          const data = await response.json();
          setClients(data);
        }
      } catch (error) {
        console.error("Error loading clients:", error);
        // Fallback to localStorage if API is not available
        const saved = localStorage.getItem(LS_CLIENTS_KEY);
        if (saved) {
          try {
            setClients(JSON.parse(saved));
          } catch (e) {
            console.error("Failed to parse saved clients:", e);
          }
        }
      }
    };
    loadClients();
  }, []);

  // Load payment methods from API
  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const response = await authenticatedFetch("/api/payment-methods");
        if (response.ok) {
          const data = await response.json();
          setSavedPaymentMethods(data);
        } else {
          console.error("Failed to load payment methods:", response.status);
          const errorData = await response.json().catch(() => ({}));
          console.error("Error details:", errorData);
          // Fallback to localStorage if API is not available
          const saved = localStorage.getItem(LS_PAYMENT_METHODS_KEY);
          if (saved) {
            try {
              setSavedPaymentMethods(JSON.parse(saved));
            } catch (e) {
              console.error("Failed to parse saved payment methods:", e);
            }
          }
        }
      } catch (error) {
        console.error("Error loading payment methods:", error);
        // Fallback to localStorage if API is not available
        const saved = localStorage.getItem(LS_PAYMENT_METHODS_KEY);
        if (saved) {
          try {
            setSavedPaymentMethods(JSON.parse(saved));
          } catch (e) {
            console.error("Failed to parse saved payment methods:", e);
          }
        }
      }
    };
    loadPaymentMethods();
  }, []);

  // Load custom presets from API
  useEffect(() => {
    const loadCustomPresets = async () => {
      try {
        const response = await authenticatedFetch(
          "/api/invoice-templates/presets",
        );
        if (response.ok) {
          const data = await response.json();
          setCustomPresets(data);
        }
      } catch (error) {
        console.error("Error loading custom presets:", error);
      }
    };
    loadCustomPresets();
  }, []);

  // Effect to apply default payment method once methods are loaded
  useEffect(() => {
    if (defaultPaymentMethodId && savedPaymentMethods.length > 0) {
      const defaultMethod = savedPaymentMethods.find(
        (m) => m.id === defaultPaymentMethodId,
      );
      if (defaultMethod) {
        setDraft((d) => {
          // Only apply if no payment options are set yet, or if there's only one empty option
          const hasNoOptions =
            !d.paymentOptions || d.paymentOptions.length === 0;
          const hasOneEmptyOption =
            d.paymentOptions?.length === 1 &&
            isPaymentOptionEmpty(d.paymentOptions[0]);

          if (hasNoOptions) {
            return {
              ...d,
              paymentOptions: [{ ...defaultMethod, id: crypto.randomUUID() }],
            };
          }

          if (hasOneEmptyOption) {
            // Check if the current option is already the same as default (to avoid infinite loops)
            const current = d.paymentOptions[0];
            const isSame =
              current.type === defaultMethod.type &&
              current.title === defaultMethod.title &&
              current.accountNumber === defaultMethod.accountNumber &&
              current.iban === defaultMethod.iban;

            if (!isSame) {
              return {
                ...d,
                paymentOptions: [
                  { ...defaultMethod, id: d.paymentOptions[0].id },
                ],
              };
            }
          }

          return d;
        });
      }
    }
  }, [defaultPaymentMethodId, savedPaymentMethods]);

  // Load client data when selected
  useEffect(() => {
    if (selectedClientId) {
      const client = clients.find((c) => c.id === selectedClientId);
      if (client) {
        setField("buyerName", client.name);
        setField("buyerAddress", client.address);
        setField("buyerEmail", client.email || "");
        setField("buyerPhone", client.phone || "");
        setField("buyerTaxId", client.taxId || "");
        setField("buyerId", client.buyerId || "");
        
        // Mark as unsaved since we just modified the draft
        setIsSaved(false);
      }
    }
  }, [selectedClientId, clients, setField]);

  // Helper function to normalize URLs
  const normalizeUrl = (url: string) => {
    if (!url) return "";
    // If URL already has protocol, return as is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    // If URL starts with www, add https
    if (url.startsWith("www.")) {
      return `https://${url}`;
    }
    // For other cases, add https://
    return `https://${url}`;
  };

  // Payment Options helpers
  const addPaymentOption = (type: PaymentOption["type"]) => {
    if (type === "bank") {
      setDraft((d) => ({
        ...d,
        paymentOptions: [
          ...(d.paymentOptions || []),
          {
            id: crypto.randomUUID(),
            type: "bank",
            title: currentInvoiceT.bankTransfer,
            details: "",
            accountNumber: "",
            iban: "",
            bic: "",
            bankName: "",
            bankAddress: "",
            variableSymbol: "",
            constantSymbol: "",
            specificSymbol: "",
            showQRCode: false,
          },
        ],
      }));
    } else if (type === "link") {
      setDraft((d) => ({
        ...d,
        paymentOptions: [
          ...(d.paymentOptions || []),
          {
            id: crypto.randomUUID(),
            type: "link",
            title: currentInvoiceT.paymentLink,
            details: "",
            paymentUrl: "",
          },
        ],
      }));
    } else {
      setDraft((d) => ({
        ...d,
        paymentOptions: [
          ...(d.paymentOptions || []),
          {
            id: crypto.randomUUID(),
            type,
            title: "",
            details: "",
          },
        ],
      }));
    }
  };
  const removePaymentOption = (id: string) =>
    setDraft((d) => ({
      ...d,
      paymentOptions: (d.paymentOptions || []).filter((po) => po.id !== id),
    }));
  const updatePaymentOption = (id: string, patch: Partial<PaymentOption>) =>
    setDraft((d) => ({
      ...d,
      paymentOptions: (d.paymentOptions || []).map((po) =>
        po.id === id ? { ...po, ...patch } : po,
      ),
    }));
  const updatePaymentOptionField = (
    id: string,
    field: keyof PaymentOption,
    value: any,
  ) =>
    setDraft((d) => ({
      ...d,
      paymentOptions: (d.paymentOptions || []).map((po) =>
        po.id === id ? { ...po, [field]: value } : po,
      ),
    }));

  // Amount inputs helpers
  const handleAmountChange = (id: string, value: string) => {
    const sanitized = value.replace(",", ".");
    if (sanitized === "" || /^\d*\.?\d*$/.test(sanitized)) {
      setAmountInputs((prev) => ({ ...prev, [id]: sanitized }));
      const numValue = parseFloat(sanitized);
      if (!isNaN(numValue) && isFinite(numValue)) {
        updateItem(id, { amount: numValue });
      } else {
        updateItem(id, { amount: 0 });
      }
    }
  };
  const getAmountDisplayValue = (id: string, amount?: number) => {
    if (amountInputs[id] !== undefined) return amountInputs[id];
    const safe = typeof amount === "number" && isFinite(amount) ? amount : 0;
    return safe === 0 ? "" : String(safe);
  };

  // Custom taxes helpers
  const addCustomTax = () => {
    const newTax: CustomTax = {
      id: crypto.randomUUID(),
      name: "",
      type: "percentage",
      value: 0,
    };
    setDraft((d) => ({ ...d, customTaxes: [...d.customTaxes, newTax] }));
  };
  const updateCustomTax = (
    id: string,
    field: keyof CustomTax,
    value: string | number,
  ) => {
    setDraft((d) => ({
      ...d,
      customTaxes: d.customTaxes.map((tax) =>
        tax.id === id ? { ...tax, [field]: value } : tax,
      ),
    }));
  };
  const removeCustomTax = (id: string) => {
    setDraft((d) => ({
      ...d,
      customTaxes: d.customTaxes.filter((tax) => tax.id !== id),
    }));
  };

  // Client wrappers
  const addClient = async (client: Omit<Client, "id">) => {
    try {
      await saveClient(client);
    } catch (error) {
      console.error("Failed to add client:", error);
    }
  };
  const updateClientLocal = async (id: string, client: Omit<Client, "id">) => {
    try {
      await updateClient(id, client);
    } catch (error) {
      console.error("Failed to update client:", error);
    }
  };
  const deleteClientLocal = async (id: string) => {
    try {
      await deleteClient(id);
      if (selectedClientId === id) setSelectedClientId("");
    } catch (error) {
      console.error("Failed to delete client:", error);
    }
  };

  // Saved Items helpers
  useEffect(() => {
    const fetchSavedItems = async () => {
      try {
        const response = await authenticatedFetch("/api/saved-items");
        if (response.ok) {
          const data = await response.json();
          setSavedItems(data);
        } else {
          // Fallback to local storage if backend fails
          const saved = localStorage.getItem("saved-items");
          if (saved) setSavedItems(JSON.parse(saved));
        }
      } catch (e) {
        console.error("Failed to fetch saved items:", e);
        const saved = localStorage.getItem("saved-items");
        if (saved) setSavedItems(JSON.parse(saved));
      }
    };
    fetchSavedItems();
  }, []);

  const addSavedItem = async (item: Omit<Item, "id">) => {
    const newItem = { ...item, id: crypto.randomUUID() };
    try {
      const response = await authenticatedFetch("/api/saved-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newItem),
      });
      if (response.ok) {
        const saved = await response.json();
        setSavedItems((prev) => [...prev, saved]);
      } else {
        // Fallback to local storage
        const updated = [...savedItems, newItem];
        setSavedItems(updated);
        localStorage.setItem("saved-items", JSON.stringify(updated));
      }
    } catch (e) {
      console.error("Failed to add saved item:", e);
      const updated = [...savedItems, newItem];
      setSavedItems(updated);
      localStorage.setItem("saved-items", JSON.stringify(updated));
    }
  };

  const updateSavedItem = async (id: string, patch: Omit<Item, "id">) => {
    try {
      const response = await authenticatedFetch(`/api/saved-items/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (response.ok) {
        const updated = await response.json();
        setSavedItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      } else {
        // Fallback
        const updated = savedItems.map((i) =>
          i.id === id ? { ...i, ...patch } : i
        );
        setSavedItems(updated);
        localStorage.setItem("saved-items", JSON.stringify(updated));
      }
    } catch (e) {
      console.error("Failed to update saved item:", e);
      const updated = savedItems.map((i) =>
        i.id === id ? { ...i, ...patch } : i
      );
      setSavedItems(updated);
      localStorage.setItem("saved-items", JSON.stringify(updated));
    }
  };

  const deleteSavedItem = async (id: string) => {
    try {
      const response = await authenticatedFetch(`/api/saved-items/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setSavedItems((prev) => prev.filter((i) => i.id !== id));
      } else {
        // Fallback
        const updated = savedItems.filter((i) => i.id !== id);
        setSavedItems(updated);
        localStorage.setItem("saved-items", JSON.stringify(updated));
      }
    } catch (e) {
      console.error("Failed to delete saved item:", e);
      const updated = savedItems.filter((i) => i.id !== id);
      setSavedItems(updated);
      localStorage.setItem("saved-items", JSON.stringify(updated));
    }
  };

  const selectSavedItem = (savedItem: Item) => {
    // Check if there's an empty item to replace, or add as new
    setDraft((d) => {
      const currentItems = d.items || [];
      const emptyItemIndex = currentItems.findIndex(
        (i) => !i.description.trim() && (i.amount === 0 || !i.amount)
      );

      const newItemData = {
        description: savedItem.description,
        amount: savedItem.amount || 0,
        unit: savedItem.unit || "",
        quantity: savedItem.quantity || 1,
        pricePerUnit: (savedItem as any).pricePerUnit || 0,
      };

      if (emptyItemIndex !== -1) {
        const newItems = [...currentItems];
        newItems[emptyItemIndex] = {
          ...newItems[emptyItemIndex],
          ...newItemData,
        };
        return { ...d, items: newItems };
      }

      return {
        ...d,
        items: [
          ...currentItems,
          {
            id: crypto.randomUUID(),
            ...newItemData,
          },
        ],
      };
    });
    setIsSaved(false);
  };

  // Auto-populate Payment Options from existing Payment Information and Payment Link
  const autoPopulatePaymentOptions = () => {
    const newOptions: PaymentOption[] = [];

    // Add bank transfer option if banking info exists
    if (
      draft.paymentInfo.accountNumber ||
      draft.paymentInfo.iban ||
      draft.paymentInfo.bankName
    ) {
      let bankDetails = "";
      if (draft.paymentInfo.accountNumber)
        bankDetails += `${t.invoice.account}: ${draft.paymentInfo.accountNumber}\n`;
      if (draft.paymentInfo.iban)
        bankDetails += `IBAN: ${draft.paymentInfo.iban}\n`;
      if (draft.paymentInfo.bic)
        bankDetails += `BIC/SWIFT: ${draft.paymentInfo.bic}\n`;
      if (draft.paymentInfo.bankName)
        bankDetails += `${t.invoice.bank}: ${draft.paymentInfo.bankName}\n`;
      if (draft.paymentInfo.bankAddress)
        bankDetails += `${draft.paymentInfo.bankAddress}\n`;
      if (draft.paymentInfo.variableSymbol)
        bankDetails += `${currentInvoiceT.variableSymbol}: ${draft.paymentInfo.variableSymbol}\n`;
      if (draft.paymentInfo.constantSymbol)
        bankDetails += `${currentInvoiceT.constantSymbol}: ${draft.paymentInfo.constantSymbol}`;

      newOptions.push({
        id: crypto.randomUUID(),
        type: "bank",
        title: currentInvoiceT.bankTransfer,
        details: bankDetails.trim(),
      });
    }

    // Add payment link option if payment link exists
    if (draft.paymentLink.url) {
      newOptions.push({
        id: crypto.randomUUID(),
        type: "link",
        title: currentInvoiceT.paymentLink,
        details: `${draft.paymentLink.text}: ${normalizeUrl(
          draft.paymentLink.url,
        )}`,
      });
    }

    // Add cash option
    newOptions.push({
      id: crypto.randomUUID(),
      type: "cash",
      title: currentInvoiceT.cash,
      details: "Cash payment upon delivery or pickup",
    });

    if (newOptions.length > 0) {
      setDraft((d) => ({ ...d, paymentOptions: newOptions }));
    }
  };

  const subtotal = useMemo(() => {
    return draft.items.reduce((sum, i) => {
      const amount = isFinite(i.amount) ? i.amount : 0;
      const quantity = i.quantity && i.quantity > 0 ? i.quantity : 1;
      return sum + amount * quantity;
    }, 0);
  }, [draft.items]);

  // Total for display in interface (without taxes)
  const total = useMemo(() => {
    return subtotal;
  }, [subtotal]);

  // --- HTML→PDF via backend
  const downloadPDF = async (force = false) => {
    if (isDownloadingPdf) return;

    if (!force) {
      const errors = getValidationErrors();
      if (errors.length > 0) {
        setShowValidationConfirmModal(true);
        return;
      }
    }

    setIsDownloadingPdf(true);

    try {
      // Save invoice JSON first
      await saveInvoiceJson();

      // Filter out empty payment options before generating PDF
      const filteredPaymentOptions = (draft.paymentOptions || []).filter(
        (po) => !isPaymentOptionEmpty(po),
      );

      // Generate PDF using unified function
      await generateAndDownloadPdf({
        draft: {
          ...draft,
          paymentOptions: filteredPaymentOptions,
        },
        translations: currentInvoiceT,
        designSettings,
        fontFamily: designSettings.fontFamily,
      });

      // Update download count
      await updateDownloadCount();
    } catch (error) {
      console.error("Error generating PDF:", error);

      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Не удается подключиться к серверу генерации PDF. Убедитесь, что бэкенд запущен на порту 3001.";
        } else {
          errorMessage = error.message;
        }
      }

      alert(`Ошибка генерации PDF: ${errorMessage}`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // --- Dev self-tests (lightweight "test cases")
  useEffect(() => {
    try {
      console.assert(
        formatDate("2025-09-08") === "08.09.2025",
        "formatDate failed",
      );
    } catch {}
  }, []);

  // Get status color and text
  const getStatusInfo = (
    status: "paid" | "pending" | "overdue" | undefined,
  ) => {
    switch (status) {
      case "paid":
        return {
          color:
            "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
          text: t.invoiceList.status.paid,
        };
      case "pending":
        return {
          color:
            "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
          text: t.invoiceList.status.pending,
        };
      case "overdue":
        return {
          color:
            "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
          text: t.invoiceList.status.overdue,
        };
      default:
        return {
          color:
            "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
          text: t.invoiceList.status.pending, // Default to pending text if undefined
        };
    }
  };

  const handleSendEmailManual = async (
    to: string,
    subject: string,
    text: string,
  ) => {
    if (!invoiceInternalId) return;

    try {
      const response = await authenticatedFetch("/api/send-reminder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: to || draft.buyerEmail,
          subject: subject,
          text: text,
          invoiceId: invoiceInternalId,
        }),
      });

      if (response.ok) {
        alert(t.clientNotifications.reminderSent);
        setShowEmailModal(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send email");
      }
    } catch (e: any) {
      console.error(e);
      alert(t.clientNotifications.reminderError);
    }
  };

  const getInvoiceListItem = (): InvoiceListItem => {
    const total = draft.items.reduce(
      (sum, item) => sum + (item.amount || 0) * (item.quantity || 1),
      0,
    );

    // Construct payment details string for the template
    const paymentParts: string[] = [];
    if (draft.paymentOptions && draft.paymentOptions.length > 0) {
      draft.paymentOptions.forEach((opt) => {
        if (opt.type === "bank") {
          const parts = [];
          if (opt.bankName) parts.push(opt.bankName);
          if (opt.accountNumber) parts.push(opt.accountNumber);
          if (opt.iban) parts.push(`IBAN: ${opt.iban}`);
          if (parts.length > 0) paymentParts.push(parts.join(", "));
        } else if (opt.type === "link") {
          if (opt.paymentUrl) paymentParts.push(opt.paymentUrl);
        }
      });
    } else if (draft.paymentInfo) {
      const pi = draft.paymentInfo;
      const parts = [];
      if (pi.bankName) parts.push(pi.bankName);
      if (pi.accountNumber) parts.push(pi.accountNumber);
      if (pi.iban) parts.push(`IBAN: ${pi.iban}`);
      if (parts.length > 0) paymentParts.push(parts.join(", "));
    }

    return {
      id: invoiceInternalId,
      internalId: invoiceInternalId,
      invoiceNo: draft.invoiceNo || "",
      clientName: draft.buyerName || "",
      clientEmail: draft.buyerEmail || "",
      description: draft.items[0]?.description || "",
      amount: total,
      currency: draft.currency || "CZK",
      issueDate: draft.issueDate || "",
      dueDate: draft.dueDate || "",
      status: "pending",
      createdAt: new Date().toISOString(),
      paymentDetails: paymentParts.filter(Boolean).join(" -- или -- "),
      sellerName: draft.sellerInfo?.name || "",
      variableSymbol:
        draft.paymentInfo?.variableSymbol || draft.invoiceNo || "",
    };
  };

  const currentEmailInvoice = useMemo(() => {
    if (!showEmailModal) return null;
    return getInvoiceListItem();
  }, [showEmailModal, draft, invoiceInternalId]);

  const emailInitialData = useMemo(() => {
    if (!currentEmailInvoice) return { subject: "", text: "" };
    const softTemplate = getSoftTemplate(locale);

    const fillTemplate = (template: string, inv: InvoiceListItem) => {
      const formattedAmount = `${inv.amount.toLocaleString(
        locale === "cs" ? "cs-CZ" : locale === "ru" ? "ru-RU" : "en-US",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )} ${inv.currency === "CZK" ? "Kč" : inv.currency}`;

      const formattedDate = (dateStr: string) => {
        if (!dateStr) return "";
        const [y, m, d] = dateStr.split("-");
        if (!y || !m || !d) return dateStr;
        return `${d}-${m}-${y}`;
      };

      return (template || "")
        .replace(/{invoiceNo}/g, inv.invoiceNo || "{invoiceNo}")
        .replace(/{issueDate}/g, formattedDate(inv.issueDate) || "{issueDate}")
        .replace(/{amount}/g, formattedAmount || "{amount}")
        .replace(/{paymentDetails}/g, inv.paymentDetails || "{paymentDetails}")
        .replace(/{companyName}/g, inv.sellerName || "{companyName}");
    };

    return {
      subject: fillTemplate(softTemplate.subject, currentEmailInvoice),
      text: fillTemplate(softTemplate.body, currentEmailInvoice),
    };
  }, [currentEmailInvoice, locale]);

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-900 transition-colors p-4 xl:p-6">
      {/* Header */}
      <header className="z-40 bg-white dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700/50 shadow-sm rounded-2xl mb-6 transition-colors max-w-7xl mx-auto overflow-visible">
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-y-4 gap-x-2">
          {/* LEFT SIDE: Actions & Status */}
          <div className="flex items-center gap-2 flex-wrap">
            <SaveButton
              id="tour-save"
              onClick={saveInvoice}
              isSaving={isSaving}
              disabled={!draft.invoiceNo}
              text={t.invoice.save}
            />

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center px-2 min-w-[90px] justify-center">
                {isSaving ? (
                  <span className="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-400 animate-pulse flex-shrink-0" />
                    <span className="break-words leading-tight">
                      {t.common.saving}...
                    </span>
                  </span>
                ) : isSaved ? (
                  <span className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="break-words leading-tight">
                      {t.invoice.saved}
                    </span>
                  </span>
                ) : (
                  <span className="text-sm font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0" />
                    <span className="break-words leading-tight">
                      {t.invoice.notSaved}
                    </span>
                  </span>
                )}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => downloadPDF()}
                disabled={isDownloadingPdf || !draft.invoiceNo}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg ${
                  isDownloadingPdf || !draft.invoiceNo
                    ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed shadow-none hover:shadow-none"
                    : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500"
                }`}
              >
                {isDownloadingPdf ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : (
                  <Download className="h-4 w-4 flex-shrink-0" />
                )}
                <span className="break-words leading-tight">
                  {t.invoice.downloadPdf}
                </span>
              </motion.button>

              <div className="relative group flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    // Always save before sharing to ensure latest changes are included
                    await saveInvoice({ silent: true });
                    setShowShareModal(true);
                  }}
                  disabled={isSaving || !draft.invoiceNo}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg ${
                    isSaving || !draft.invoiceNo
                      ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed shadow-none hover:shadow-none"
                      : "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                  }`}
                >
                  <Share2 className="h-4 w-4 flex-shrink-0" />
                  <span className="break-words leading-tight">
                    {locale === "ru"
                      ? "Поделиться"
                      : locale === "cs"
                        ? "Sdílet"
                        : "Share"}
                  </span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    // Always save before sending to ensure latest changes are included
                    await saveInvoice({ silent: true });

                    if (!isEmailConfigured) {
                      navigate(`/${locale}/client-notifications`);
                    } else {
                      setShowEmailModal(true);
                    }
                  }}
                  disabled={isSaving || !draft.invoiceNo}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg ${
                    isSaving || !draft.invoiceNo
                      ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed shadow-none hover:shadow-none"
                      : "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                  }`}
                >
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span className="break-words leading-tight">
                    {t.invoice.sendByEmail}
                  </span>
                </motion.button>
              </div>
            </div>

            {showValidationConfirmModal && (
              <div
                className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-slate-900/50 backdrop-blur-sm"
                onMouseDown={(e) =>
                  setMouseDownOnOverlay(e.target === e.currentTarget)
                }
                onMouseUp={(e) => {
                  if (mouseDownOnOverlay && e.target === e.currentTarget) {
                    setShowValidationConfirmModal(false);
                  }
                  setMouseDownOnOverlay(false);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white dark:bg-slate-800 rounded-[2rem] p-5 sm:p-8 shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-3 mb-4 text-amber-600">
                    <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6" />
                    <h3 className="text-lg sm:text-xl font-bold dark:text-white">
                      {t.invoice.validation.modalTitle}
                    </h3>
                  </div>
                  <div className="mb-6 text-sm sm:text-base">
                    {validationMessage?.render(true)}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        setShowValidationConfirmModal(false);
                        setShowFieldErrors(true);
                      }}
                      className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-2xl transition-colors text-sm sm:text-base order-2 sm:order-1"
                    >
                      {t.invoice.validation.fixButton}
                    </button>
                    <button
                      onClick={() => {
                        setShowValidationConfirmModal(false);
                        downloadPDF(true);
                      }}
                      className="flex-1 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-md shadow-indigo-200 dark:shadow-none text-sm sm:text-base order-1 sm:order-2"
                    >
                      {t.invoice.validation.downloadButton}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}

            {showSaveValidationModal && (
              <div
                className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-slate-900/50 backdrop-blur-sm"
                onMouseDown={(e) =>
                  setMouseDownOnOverlay(e.target === e.currentTarget)
                }
                onMouseUp={(e) => {
                  if (mouseDownOnOverlay && e.target === e.currentTarget) {
                    setShowSaveValidationModal(false);
                  }
                  setMouseDownOnOverlay(false);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white dark:bg-slate-800 rounded-[2rem] p-5 sm:p-8 shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-3 mb-4 text-indigo-600">
                    <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6" />
                    <h3 className="text-lg sm:text-xl font-bold dark:text-white">
                      {t.invoice.validation.modalTitle}
                    </h3>
                  </div>
                  <div className="mb-6 text-sm sm:text-base">
                    {validationMessage?.render(false)}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowSaveValidationModal(false);
                        setShowFieldErrors(true);
                      }}
                      className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl transition-all shadow-md shadow-green-200 dark:shadow-none text-sm sm:text-base"
                    >
                      {t.invoice.validation.okButton}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE: Status Selector */}
          <div className="flex items-center gap-2 justify-end flex-wrap ml-auto">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap">
              {t.invoiceList.invoiceStatus}
            </span>
            <div id="tour-status-selector" className="w-36">
              <CustomDropdown
                options={[
                  {
                    value: "pending",
                    label: (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          getStatusInfo("pending").color
                        }`}
                      >
                        {t.invoiceList.status.pending}
                      </span>
                    ),
                  },
                  {
                    value: "paid",
                    label: (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          getStatusInfo("paid").color
                        }`}
                      >
                        {t.invoiceList.status.paid}
                      </span>
                    ),
                  },
                  {
                    value: "overdue",
                    label: (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          getStatusInfo("overdue").color
                        }`}
                      >
                        {t.invoiceList.status.overdue}
                      </span>
                    ),
                  },
                ]}
                value={draft.status || "pending"}
                onChange={(value) => setField("status", value)}
                placeholder={t.invoice.invoiceStatus || "Invoice Status"}
                renderValue={(value) => {
                  const info = getStatusInfo(
                    value as "paid" | "pending" | "overdue",
                  );
                  return (
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold border ${info.color}`}
                    >
                      {info.text}
                    </span>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl grid-cols-1 xl:grid-cols-[4.5fr_5.5fr] gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          <div className="flex justify-end gap-3 mb-4">
            <button
              id="tour-restart-button"
              onClick={startTour}
              className="px-4 py-2 border-2 border-sky-400 text-sky-600 hover:bg-sky-50 dark:border-sky-500 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-xl transition-all flex items-center gap-2 shadow-sm active:scale-95"
              title="Restart Tutorial"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-wider">
                {t.invoice.onboarding.tutorialButton}
              </span>
            </button>

            <button
              onClick={() => {
                setSettingsDialogTab("general");
                setShowSettingsModal(true);
              }}
              className="px-4 py-2 border-2 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all flex items-center gap-2 shadow-sm active:scale-95"
            >
              <span className="text-sm font-bold uppercase tracking-wider">
                {t.invoiceList.settings.title}
              </span>
              <Settings className="h-5 w-5" />
            </button>
          </div>
          {/* 1. Invoice Basics */}
          <div
            id="tour-basics"
            className={`bg-white dark:bg-slate-800 rounded-2xl border ${
              showFieldErrors && basicsErrorMessage
                ? "border-red-200 dark:border-red-800"
                : "border-slate-200 dark:border-slate-700"
            } shadow-sm transition-colors`}
          >
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {t.invoice.invoiceBasics || "Invoice Basics"}
              </h3>
            </div>
            <div className="p-6 space-y-6">
              {showFieldErrors && basicsErrorMessage && (
                <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{basicsErrorMessage}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-6 items-end">
                {/* Language & Number */}
                <div className="space-y-2 w-full min-[580px]:max-w-[180px]">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t.invoice.invoiceLanguage}
                  </label>
                  <select
                    value={invoiceLanguage}
                    onChange={(e) =>
                      setInvoiceLanguage(e.target.value as LanguageCode)
                    }
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-slate-100 iv-select"
                  >
                    {Object.entries(languages).map(([code, lang]) => (
                      <option key={code} value={code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2 w-full min-[580px]:flex-1 min-[580px]:w-auto">
                  <label
                    className={getLabelClass(
                      "invoiceNo",
                      "text-sm font-medium flex-shrink-0",
                    )}
                  >
                    {t.invoice.invoiceNumber}
                  </label>
                  <div className="flex gap-2">
                    <input
                      value={draft.invoiceNo}
                      onChange={(e) =>
                        setField("invoiceNo", sanitizeInput(e.target.value))
                      }
                      className={getInputClass(
                        "invoiceNo",
                        "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-slate-100 placeholder-slate-400",
                      )}
                      placeholder="INV-001"
                    />
                    <button
                      id="tour-template"
                      onClick={() => setShowInvoiceTemplateModal(true)}
                      title={t.invoice.templateButton}
                      className="p-2 aspect-square rounded-xl border-2 border-indigo-600 dark:border-indigo-500 bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 flex items-center justify-center text-white shadow-md hover:shadow-lg transition-all flex-shrink-0"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Row: Issue, Due */}
              <div className="w-full flex flex-wrap gap-6 items-end">
                <div className="space-y-2 w-full min-[580px]:flex-1 min-[580px]:w-auto">
                  <label className={getLabelClass("issueDate")}>
                    {t.invoice.issueDate}
                  </label>
                  <input
                    type="date"
                    value={draft.issueDate}
                    onChange={(e) => {
                      const newIssueDate = e.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        issueDate: newIssueDate,
                        dueDate: calculateDueDate(newIssueDate, dueDays),
                      }));
                    }}
                    className={getInputClass(
                      "issueDate",
                      "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100",
                    )}
                  />
                </div>
                <div
                  id="tour-due-date"
                  className="space-y-2 w-full min-[580px]:flex-1 min-[580px]:w-auto"
                >
                  <label className={getLabelClass("dueDate")}>
                    {t.invoice.dueDate}
                  </label>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={draft.dueDate}
                        onChange={(e) => {
                          const newDueDate = e.target.value;
                          const diff = getDaysDifference(
                            draft.issueDate,
                            newDueDate,
                          );
                          setDueDays(diff);
                          setField("dueDate", newDueDate);
                        }}
                        className={getInputClass(
                          "dueDate",
                          "w-full min-w-0 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100",
                        )}
                      />
                      <div className="relative flex-shrink-0">
                        <button
                          onClick={() =>
                            setShowDueDateSettings(!showDueDateSettings)
                          }
                          title={t.common.settings}
                          className="p-2 h-full aspect-square rounded-xl border-2 border-indigo-600 dark:border-indigo-500 bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 flex items-center justify-center text-white shadow-md hover:shadow-lg transition-all"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <AnimatePresence>
                          {showDueDateSettings && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -10 }}
                              className="absolute right-0 mt-2 p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl z-50 w-56"
                            >
                              <label className="block text-xs font-bold mb-2 text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                {t.invoice.daysUntilDue}
                              </label>
                              <div className="flex gap-2 mb-3">
                                <input
                                  type="number"
                                  value={dueDays}
                                  onChange={(e) => {
                                    const days = parseInt(e.target.value) || 0;
                                    setDueDays(days);
                                    setField(
                                      "dueDate",
                                      calculateDueDate(draft.issueDate, days),
                                    );
                                  }}
                                  className="flex-1 min-w-0 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                />
                              </div>
                              <button
                                onClick={() => setShowDueDateSettings(false)}
                                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 transition-all"
                              >
                                {t.common.apply}
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Parties */}
          <div
            id="tour-parties"
            className={`bg-white dark:bg-slate-800 rounded-2xl border ${
              showFieldErrors && partiesErrorMessage
                ? partiesErrorMessage.errors
                  ? "border-red-200 dark:border-red-800"
                  : "border-amber-200 dark:border-amber-800"
                : "border-slate-200 dark:border-slate-700"
            } shadow-sm transition-colors`}
          >
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {t.invoice.parties || "Parties"}
              </h3>
            </div>
            <div className="p-6">
              {showFieldErrors && partiesErrorMessage && (
                <>
                  {partiesErrorMessage.errors}
                  {partiesErrorMessage.warnings}
                </>
              )}
              <div className="flex rounded-xl bg-slate-200 dark:bg-slate-700 p-1 mb-6 border border-slate-300 dark:border-slate-600">
                <button
                  onClick={() => setActiveTab("customer")}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${
                    activeTab === "customer"
                      ? "bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white border border-slate-300 dark:border-slate-500"
                      : (partiesErrorMessage as any)?.hasCustomerErrors
                        ? "bg-red-500 text-white shadow-md font-bold border border-red-600 animate-in fade-in zoom-in-95"
                        : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white border border-transparent"
                  }`}
                >
                  {t.invoice.customer}
                </button>
                <button
                  onClick={() => setActiveTab("seller")}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${
                    activeTab === "seller"
                      ? "bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white border border-slate-300 dark:border-slate-500"
                      : (partiesErrorMessage as any)?.hasSellerErrors
                        ? "bg-red-500 text-white shadow-md font-bold border border-red-600 animate-in fade-in zoom-in-95"
                        : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white border border-transparent"
                  }`}
                >
                  {t.invoice.sellerInfo}
                </button>
              </div>

              {activeTab === "customer" ? (
                <div className="space-y-4">
                  {/* Client selection block - separate row */}
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex-1 min-w-[120px]">
                        {t.clientDialog.title}
                      </label>
                      <button
                        id="tour-manage-clients"
                        onClick={() => setShowClientDialog(true)}
                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-700 break-words leading-tight max-w-full"
                      >
                        {t.invoice.manageClients || t.invoice.manageButton}
                      </button>
                    </div>
                    <SearchableDropdown
                      options={clients.map((c) => ({
                        value: c.id,
                        label: c.name,
                        sublabel: c.taxId || c.email,
                      }))}
                      value={selectedClientId}
                      onChange={(val) => setSelectedClientId(val)}
                      placeholder={
                        t.clientDialog.selectClient || t.common.select
                      }
                      searchPlaceholder={t.common.search || "Search..."}
                      noOptionsText={
                        t.invoiceList.noInvoices || "No clients found"
                      }
                    />
                  </div>

                  <div className="border-t border-slate-300 dark:border-slate-600 my-2" />

                  {/* Other customer fields */}
                  <div className="grid grid-cols-1 min-[680px]:grid-cols-2 gap-4">
                    <div className="min-[680px]:col-span-2 space-y-2">
                      <label className={getLabelClass("buyerName")}>
                        {t.common.name}
                      </label>
                      <input
                        value={draft.buyerName}
                        onChange={(e) =>
                          setField("buyerName", sanitizeInput(e.target.value))
                        }
                        className={getInputClass(
                          "buyerName",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                        )}
                        placeholder="Company / Name"
                      />
                    </div>
                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/10 space-y-3">
                      <label className={getLabelClass("buyerTaxId")}>
                        {t.invoice.identificationNumber}
                      </label>
                      <div className="space-y-3">
                        <input
                          value={draft.buyerId || ""}
                          onChange={(e) =>
                            setField("buyerId", sanitizeInput(e.target.value))
                          }
                          className={getInputClass(
                            "buyerTaxId",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                          )}
                          placeholder="17653223"
                        />
                        <button
                          type="button"
                          onClick={() => handleAresLookup(true)}
                          className="w-full py-2.5 px-3 rounded-xl border border-purple-200 dark:border-purple-900/50 bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                          disabled={aresLoading}
                        >
                          {aresLoading ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : null}
                          {aresLoading
                            ? t.invoice.validation.aresLoading
                            : t.invoice.validation.aresButton}
                        </button>
                        {aresStatus !== "idle" && (
                          <div
                            className={`text-[10px] font-medium px-2 py-1 rounded-lg inline-block ${
                              aresStatus === "success"
                                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                                : aresStatus === "error" &&
                                    !isValidIco(draft.buyerId)
                                  ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                                  : "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                            }`}
                          >
                            {aresStatus === "success" && t.common.success}
                            {aresStatus === "error" &&
                              !isValidIco(draft.buyerId) &&
                              t.invoice.validation.aresMinChars}
                            {aresStatus === "not_found" &&
                              t.invoice.validation.aresNotFound}
                            {aresStatus === "error" &&
                              isValidIco(draft.buyerId) &&
                              t.invoice.validation.aresError}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 pt-4">
                      <label className={getLabelClass("buyerVatNumber")}>
                        {t.invoice.dic}
                      </label>
                      <input
                        value={draft.buyerTaxId || ""}
                        onChange={(e) =>
                          setField("buyerTaxId", sanitizeInput(e.target.value))
                        }
                        className={getInputClass(
                          "buyerVatNumber",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                        )}
                        placeholder="DIČ"
                      />
                      <div className="pt-2">
                        <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                            {t.invoice.vatPayer}
                          </span>
                          <select
                            value={
                              draft.buyerIsVatPayer === undefined ||
                              draft.buyerIsVatPayer === null
                                ? "not-specified"
                                : draft.buyerIsVatPayer
                                  ? "yes"
                                  : "no"
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setDraft((d) => ({
                                ...d,
                                buyerIsVatPayer:
                                  v === "not-specified" ? null : v === "yes",
                              }));
                            }}
                            className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100 iv-select"
                          >
                            <option value="not-specified">
                              {t.invoice.notSpecified}
                            </option>
                            <option value="yes">
                              {t.invoice.vatPayer.replace("?", "")}
                            </option>
                            <option value="no">{t.invoice.nonVatPayer}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="min-[680px]:col-span-2 space-y-2 pt-4">
                      <label className={getLabelClass("buyerAddress")}>
                        {t.invoice.address}
                      </label>
                      <textarea
                        value={draft.buyerAddress}
                        onChange={(e) =>
                          setField(
                            "buyerAddress",
                            sanitizeInput(e.target.value),
                          )
                        }
                        className={getInputClass(
                          "buyerAddress",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm min-h-[80px] text-slate-900 dark:text-slate-100 placeholder-slate-400",
                        )}
                        placeholder="Address"
                      />
                    </div>
                    <div className="min-[680px]:col-span-2 grid grid-cols-1 min-[680px]:grid-cols-2 gap-4">
                      <div className="space-y-2 pt-4">
                        <label className={getLabelClass("buyerEmail")}>
                          {t.clientDialog.email}
                        </label>
                        <input
                          type="email"
                          value={draft.buyerEmail || ""}
                          onChange={(e) =>
                            setField(
                              "buyerEmail",
                              sanitizeInput(e.target.value),
                            )
                          }
                          className={getInputClass(
                            "buyerEmail",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                          )}
                          placeholder="Email"
                        />
                      </div>
                      <div className="space-y-2 pt-4">
                        <label className={getLabelClass("buyerPhone")}>
                          {t.clientDialog.phone}
                        </label>
                        <input
                          type="tel"
                          value={draft.buyerPhone || ""}
                          onChange={(e) =>
                            setField(
                              "buyerPhone",
                              sanitizeInput(e.target.value),
                            )
                          }
                          className={getInputClass(
                            "buyerPhone",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                          )}
                          placeholder="Phone"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={openSellerSettings}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-700 whitespace-nowrap flex-shrink-0"
                    >
                      {t.invoice.loadDefaults || "Configure default data"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 min-[680px]:grid-cols-2 gap-4">
                    <div className="min-[680px]:col-span-2 space-y-2">
                      <label className={getLabelClass("sellerName")}>
                        {t.common.name}
                      </label>
                      <input
                        value={draft.sellerInfo.name}
                        onChange={(e) =>
                          setSellerField("name", sanitizeInput(e.target.value))
                        }
                        className={getInputClass(
                          "sellerName",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100",
                        )}
                      />
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/10 space-y-3">
                      <label className={getLabelClass("sellerTaxId")}>
                        {t.invoice.identificationNumber}
                      </label>
                      <div className="space-y-3">
                        <input
                          value={draft.sellerInfo.identificationNumber}
                          onChange={(e) =>
                            setSellerField(
                              "identificationNumber",
                              sanitizeInput(e.target.value),
                            )
                          }
                          className={getInputClass(
                            "sellerTaxId",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                          )}
                          placeholder="17653223"
                        />
                        <button
                          type="button"
                          onClick={() => handleAresLookup(false)}
                          className="w-full py-2.5 px-3 rounded-xl border border-purple-200 dark:border-purple-900/50 bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                          disabled={aresLoading}
                        >
                          {aresLoading ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : null}
                          {aresLoading
                            ? t.invoice.validation.aresLoading
                            : t.invoice.validation.aresButton}
                        </button>
                        {aresStatus !== "idle" && (
                          <div
                            className={`text-[10px] font-medium px-2 py-1 rounded-lg inline-block ${
                              aresStatus === "success"
                                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                                : aresStatus === "error" &&
                                    !isValidIco(
                                      draft.sellerInfo.identificationNumber,
                                    )
                                  ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                                  : "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                            }`}
                          >
                            {aresStatus === "success" && t.common.success}
                            {aresStatus === "error" &&
                              !isValidIco(
                                draft.sellerInfo.identificationNumber,
                              ) &&
                              t.invoice.validation.aresMinChars}
                            {aresStatus === "not_found" &&
                              t.invoice.validation.aresNotFound}
                            {aresStatus === "error" &&
                              isValidIco(
                                draft.sellerInfo.identificationNumber,
                              ) &&
                              t.invoice.validation.aresError}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 pt-4">
                      <label className={getLabelClass("sellerVatNumber")}>
                        {t.invoice.dic}
                      </label>
                      <input
                        value={draft.sellerInfo.dic || ""}
                        onChange={(e) =>
                          setSellerField("dic", sanitizeInput(e.target.value))
                        }
                        className={getInputClass(
                          "sellerVatNumber",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100",
                        )}
                        placeholder="DIČ"
                      />
                      <div className="pt-2">
                        <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                            {t.invoice.vatPayer}
                          </span>
                          <select
                            value={
                              draft.sellerInfo.isVatPayer === undefined ||
                              draft.sellerInfo.isVatPayer === null
                                ? ""
                                : String(draft.sellerInfo.isVatPayer)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              const isPayer =
                                val === "" ? null : val === "true";
                              setSellerField("isVatPayer", isPayer);
                              if (isPayer !== null) {
                                setDraft((d) => ({
                                  ...d,
                                  vatEnabled: isPayer,
                                }));
                              }
                            }}
                            className="w-full px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100 iv-select"
                          >
                            <option value="">{t.invoice.notSpecified}</option>
                            <option value="true">
                              {t.invoice.vatPayer.replace("?", "")}
                            </option>
                            <option value="false">
                              {t.invoice.nonVatPayer}
                            </option>
                          </select>
                        </div>
                      </div>

                      {/* Czech Sole Proprietor Toggle - moved here */}
                      <div className="pt-2">
                        <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                {t.invoice.czechSoleProprietor}
                              </span>
                              <div
                                className="relative flex items-center"
                                onMouseEnter={() =>
                                  setShowCzechSoleProprietorTooltip(true)
                                }
                                onMouseLeave={() =>
                                  setShowCzechSoleProprietorTooltip(false)
                                }
                              >
                                <HelpCircle
                                  size={14}
                                  className="text-slate-400 cursor-help"
                                />
                                <AnimatePresence>
                                  {showCzechSoleProprietorTooltip && (
                                    <motion.div
                                      initial={{
                                        opacity: 0,
                                        y: 10,
                                        scale: 0.95,
                                      }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-bold rounded-xl shadow-xl z-50 pointer-events-none w-64 text-center border border-slate-700/50"
                                    >
                                      {t.invoice.czechSoleProprietorTooltip}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                            <Toggle
                              checked={
                                draft.sellerInfo.isCzechSoleProprietor || false
                              }
                              onChange={(checked) =>
                                setSellerField("isCzechSoleProprietor", checked)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="min-[680px]:col-span-2 space-y-2 pt-4">
                      <label className={getLabelClass("sellerAddress")}>
                        {t.invoice.address}
                      </label>
                      <textarea
                        value={draft.sellerInfo.address}
                        onChange={(e) =>
                          setSellerField(
                            "address",
                            sanitizeInput(e.target.value),
                          )
                        }
                        className={getInputClass(
                          "sellerAddress",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm min-h-[80px] text-slate-900 dark:text-slate-100",
                        )}
                        placeholder="Address"
                      />
                    </div>

                    <div className="min-[680px]:col-span-2 grid grid-cols-1 min-[680px]:grid-cols-2 gap-4">
                      <div className="space-y-2 pt-4">
                        <label className={getLabelClass("sellerWebsite")}>
                          {t.invoice.website}
                        </label>
                        <input
                          value={draft.sellerInfo.website || ""}
                          onChange={(e) =>
                            setSellerField(
                              "website",
                              sanitizeInput(e.target.value),
                            )
                          }
                          className={getInputClass(
                            "sellerWebsite",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100",
                          )}
                          placeholder="https://example.com"
                        />
                      </div>
                      <div className="space-y-2 pt-4">
                        <label className={getLabelClass("sellerEmail")}>
                          {t.clientDialog.email}
                        </label>
                        <input
                          type="email"
                          value={draft.sellerInfo.email || ""}
                          onChange={(e) =>
                            setSellerField(
                              "email",
                              sanitizeInput(e.target.value),
                            )
                          }
                          className={getInputClass(
                            "sellerEmail",
                            "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400",
                          )}
                          placeholder="Email"
                        />
                      </div>
                    </div>

                    <div className="min-[680px]:col-span-2 space-y-2 pt-4">
                      <label className={getLabelClass("sellerNotes")}>
                        {t.invoice.notes}
                      </label>
                      <textarea
                        value={draft.sellerInfo.notes}
                        onChange={(e) =>
                          setSellerField("notes", sanitizeInput(e.target.value))
                        }
                        className={getInputClass(
                          "sellerNotes",
                          "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm min-h-[80px] text-slate-900 dark:text-slate-100",
                        )}
                        placeholder="Notes"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* 3. Tax Settings */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {t.invoice.taxSettings}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-sm text-slate-700 dark:text-slate-300">
                    {t.invoice.enableVat}
                  </span>
                  <Toggle
                    checked={draft.vatEnabled}
                    onChange={(c) =>
                      setDraft((d) => ({
                        ...d,
                        vatEnabled: c,
                        sellerInfo: { ...d.sellerInfo, isVatPayer: c },
                      }))
                    }
                  />
                </div>
                {draft.vatEnabled && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">
                      {t.invoice.vatRate}
                    </span>
                    <input
                      type="number"
                      value={draft.vatRate || ""}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          vatRate: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="w-20 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t.invoice.addCustomTax}
                  </label>
                  <button
                    onClick={addCustomTax}
                    className="text-xs bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 px-2 py-1 rounded transition-colors"
                  >
                    + {t.invoice.addCustomTax}
                  </button>
                </div>
                <div className="space-y-2">
                  {draft.customTaxes.map((tax) => (
                    <div
                      key={tax.id}
                      className="grid grid-cols-12 gap-2 items-center bg-slate-50 dark:bg-slate-900 p-2 rounded"
                    >
                      <input
                        value={tax.name}
                        onChange={(e) =>
                          updateCustomTax(tax.id, "name", e.target.value)
                        }
                        className="col-span-5 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        placeholder={t.invoice.taxName}
                      />
                      <select
                        value={tax.type}
                        onChange={(e) =>
                          updateCustomTax(tax.id, "type", e.target.value)
                        }
                        className="col-span-3 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 iv-select"
                      >
                        <option value="percentage">
                          {t.invoice.percentage}
                        </option>
                        <option value="fixed">{t.invoice.fixedAmount}</option>
                      </select>
                      <input
                        type="number"
                        value={tax.value}
                        onChange={(e) =>
                          updateCustomTax(
                            tax.id,
                            "value",
                            parseFloat(e.target.value),
                          )
                        }
                        className="col-span-3 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        placeholder={t.invoice.taxAmount}
                      />
                      <button
                        onClick={() => removeCustomTax(tax.id)}
                        className="col-span-1 p-2 text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-xl transition-colors border border-red-100 dark:border-red-900/50 flex justify-center items-center"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 4. Items */}
          <div
            id="tour-items"
            className={`bg-white dark:bg-slate-800 rounded-2xl border ${
              showFieldErrors && itemsErrorMessage
                ? "border-red-200 dark:border-red-800"
                : "border-slate-200 dark:border-slate-700"
            } shadow-sm transition-colors`}
          >
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {t.invoice.items}
              </h3>
            </div>
            <div className="p-6 overflow-visible relative z-[5]">
              {/* Currency Selection */}
              <div className="mb-6">
                <div className="space-y-2 max-w-[220px]">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t.common.currency}
                  </label>
                  <select
                    value={draft.currencyKey || ""}
                    onChange={(e) => {
                      const selectedKey = e.target.value;
                      const currencyCode =
                        currencyNames[selectedKey as any] || "";
                      setDraft((d) => ({
                        ...d,
                        currency: currencyCode,
                        currencyKey: selectedKey,
                      }));
                    }}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-slate-100 iv-select"
                  >
                    <option value="">-- {t.common.select} --</option>
                    {getSortedCurrencies(locale).map(({ key, code, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showFieldErrors && itemsErrorMessage && (
                <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{itemsErrorMessage}</span>
                </div>
              )}
              <ItemsEditor
                items={draft.items}
                currency={draft.currency}
                showErrors={showFieldErrors}
                getValidationErrors={getValidationErrors}
                interfaceT={{
                  items: t.invoice.items,
                  description: t.common.description,
                  quantity: t.invoice.quantity,
                  unit: t.invoice.unit,
                  amount: t.invoice.price,
                  pricePerUnit: t.invoice.pricePerUnit,
                  total: t.common.total,
                  addItem: t.invoice.addItem,
                  manageSavedItems: t.invoice.manageSavedItems,
                  descriptionPlaceholder: t.invoice.descriptionPlaceholder,
                  unitPlaceholder: t.invoice.unitPlaceholder,
                  units: t.invoice.units,
                }}
                updateItem={updateItem}
                removeItem={removeItem}
                addItem={addItem}
                getAmountDisplayValue={getAmountDisplayValue}
                handleAmountChange={handleAmountChange}
                savedItems={savedItems}
                onManageSavedItems={() => setShowItemsDialog(true)}
                onSelectSavedItem={selectSavedItem}
              />
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {t.invoice.subtotal}
                  </span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {formatAmount(subtotal, draft.currency)}
                  </span>
                </div>
                {draft.vatEnabled && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {t.invoice.vat} ({draft.vatRate}%)
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {formatAmount(
                        (subtotal * (draft.vatRate || 0)) / 100,
                        draft.currency,
                      )}
                    </span>
                  </div>
                )}
                {draft.customTaxes.map((ct) => (
                  <div
                    key={ct.id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {ct.name}
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {formatAmount(
                        ct.type === "percentage"
                          ? (subtotal * ct.value) / 100
                          : ct.value,
                        draft.currency,
                      )}
                    </span>
                  </div>
                ))}
                <div className="border-t border-dashed border-slate-300 dark:border-slate-700 my-3" />
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {t.invoice.grandTotal}
                  </span>
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {formatAmount(
                      subtotal +
                        (draft.vatEnabled
                          ? (subtotal * (draft.vatRate || 0)) / 100
                          : 0) +
                        draft.customTaxes.reduce(
                          (acc, ct) =>
                            acc +
                            (ct.type === "percentage"
                              ? (subtotal * ct.value) / 100
                              : ct.value),
                          0,
                        ),
                      draft.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Payment Options */}
          <div
            id="tour-payment"
            className={`bg-white dark:bg-slate-800 rounded-2xl border ${
              showFieldErrors && paymentErrorMessage
                ? "border-red-200 dark:border-red-800"
                : "border-slate-200 dark:border-slate-700"
            } shadow-sm transition-colors`}
          >
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/50 rounded-t-2xl">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {t.invoice.paymentOptions}
              </h3>
            </div>
            <div className="p-6 space-y-4 overflow-visible">
              {showFieldErrors && paymentErrorMessage && (
                <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{paymentErrorMessage}</span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1 min-w-0">
                  <SearchableDropdown
                    options={savedPaymentMethods.map((method) => ({
                      value: method.id,
                      label: method.title,
                      sublabel:
                        method.type === "bank"
                          ? t.invoice.bankTransfer
                          : method.type === "link"
                            ? t.invoice.paymentLink
                            : method.type === "cash"
                              ? t.invoice.cash
                              : t.invoice.custom,
                    }))}
                    value=""
                    onChange={(val) => {
                      if (val) addPaymentMethodFromSaved(val);
                    }}
                    placeholder={
                      t.invoice.selectPaymentMethod || t.common.select
                    }
                    searchPlaceholder={t.common.search || "Search..."}
                    noOptionsText={
                      t.invoice.noSavedMethods || "No methods found"
                    }
                    iconType="payment"
                  />
                </div>
                <button
                  onClick={() => setShowPaymentMethodDialog(true)}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-700 whitespace-nowrap flex-shrink-0"
                >
                  {t.invoice.savedPaymentMethods ||
                    t.paymentMethodDialog.savedMethods}
                </button>
              </div>

              <div className="border-t border-slate-300 dark:border-slate-600 my-2" />

              <div className="space-y-3">
                {draft.paymentOptions?.map((po) => {
                  const typeLabel =
                    po.type === "bank"
                      ? t.invoice.bankTransfer
                      : po.type === "link"
                        ? t.invoice.paymentLink
                        : po.type === "cash"
                          ? t.invoice.cash
                          : t.invoice.custom;

                  return (
                    <div
                      key={po.id}
                      className="bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-100 dark:border-slate-700 overflow-visible"
                    >
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <span className="text-xs font-bold uppercase text-slate-700 dark:text-slate-100 flex-1 min-w-0 break-words">
                          {typeLabel}
                        </span>
                        <button
                          onClick={() => removePaymentOption(po.id)}
                          className="p-2 text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-xl transition-colors border border-red-100 dark:border-red-900/50 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <input
                        value={po.title}
                        onChange={(e) =>
                          updatePaymentOption(po.id, { title: e.target.value })
                        }
                        className="w-full mb-2 px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-sm font-medium text-slate-900 dark:text-slate-100"
                        placeholder={t.invoice.paymentTitle}
                      />
                      {po.type === "bank" && (
                        <div className="space-y-2">
                          <input
                            value={po.accountNumber || ""}
                            onChange={(e) =>
                              updatePaymentOptionField(
                                po.id,
                                "accountNumber",
                                sanitizeInput(e.target.value),
                              )
                            }
                            className={getInputClass(
                              `paymentBank_${po.id}`,
                              "w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100",
                            )}
                            placeholder={t.paymentMethodDialog.accountNumber}
                          />
                          <input
                            value={po.iban || ""}
                            onChange={(e) =>
                              updatePaymentOptionField(
                                po.id,
                                "iban",
                                sanitizeInput(e.target.value),
                              )
                            }
                            className={getInputClass(
                              `paymentBank_${po.id}`,
                              "w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100",
                            )}
                            placeholder={t.paymentMethodDialog.iban}
                          />
                          <input
                            value={po.bic || ""}
                            onChange={(e) =>
                              updatePaymentOptionField(
                                po.id,
                                "bic",
                                sanitizeInput(e.target.value),
                              )
                            }
                            className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100"
                            placeholder={t.paymentMethodDialog.bicSwift}
                          />
                          <div className="flex flex-wrap gap-2">
                            <input
                              value={po.variableSymbol || ""}
                              onChange={(e) =>
                                updatePaymentOptionField(
                                  po.id,
                                  "variableSymbol",
                                  sanitizeInput(e.target.value),
                                )
                              }
                              className="flex-1 min-w-[120px] px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100"
                              placeholder={t.paymentMethodDialog.variableSymbol}
                            />
                            <input
                              value={po.constantSymbol || ""}
                              onChange={(e) =>
                                updatePaymentOptionField(
                                  po.id,
                                  "constantSymbol",
                                  sanitizeInput(e.target.value),
                                )
                              }
                              className="flex-1 min-w-[120px] px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100"
                              placeholder={t.paymentMethodDialog.constantSymbol}
                            />
                            <input
                              value={po.specificSymbol || ""}
                              onChange={(e) =>
                                updatePaymentOptionField(
                                  po.id,
                                  "specificSymbol",
                                  sanitizeInput(e.target.value),
                                )
                              }
                              className="flex-1 min-w-[120px] px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100"
                              placeholder={t.paymentMethodDialog.specificSymbol}
                            />
                          </div>
                          <input
                            value={po.bankName || ""}
                            onChange={(e) =>
                              updatePaymentOptionField(
                                po.id,
                                "bankName",
                                sanitizeInput(e.target.value),
                              )
                            }
                            className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100"
                            placeholder={t.paymentMethodDialog.bankName}
                          />
                          <div className="flex items-center gap-3 mt-2">
                            <Toggle
                              checked={po.showQRCode || false}
                              onChange={(checked) =>
                                updatePaymentOption(po.id, {
                                  showQRCode: checked,
                                })
                              }
                            />
                            <span className="text-xs text-slate-700 dark:text-slate-300">
                              {t.invoice.showQRCode}
                            </span>
                          </div>
                        </div>
                      )}
                      {po.type === "link" && (
                        <div className="space-y-2">
                          <input
                            type="url"
                            value={po.paymentUrl || ""}
                            onChange={(e) =>
                              updatePaymentOptionField(
                                po.id,
                                "paymentUrl",
                                sanitizeInput(e.target.value),
                              )
                            }
                            className={getInputClass(
                              `paymentLink_${po.id}`,
                              "w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-900 dark:text-slate-100",
                            )}
                            placeholder="https://payment.example.com"
                          />
                        </div>
                      )}
                      {(po.type === "cash" || po.type === "custom") && (
                        <div className="space-y-2">
                          <textarea
                            value={po.details || ""}
                            onChange={(e) =>
                              updatePaymentOption(po.id, {
                                details: e.target.value,
                              })
                            }
                            className={getInputClass(
                              `paymentCustom_${po.id}`,
                              "w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs min-h-[60px] text-slate-900 dark:text-slate-100",
                            )}
                            placeholder={t.invoice.paymentDetails}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add payment type buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => addPaymentOption("bank")}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                >
                  <Plus className="w-4 h-4" />
                  {t.invoice.bankTransfer}
                </button>
                <button
                  type="button"
                  onClick={() => addPaymentOption("link")}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                >
                  <Plus className="w-4 h-4" />
                  {t.invoice.paymentLink}
                </button>
                <button
                  type="button"
                  onClick={() => addPaymentOption("cash")}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                >
                  <Plus className="w-4 h-4" />
                  {t.invoice.cash}
                </button>
                <button
                  type="button"
                  onClick={() => addPaymentOption("custom")}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                >
                  <Plus className="w-4 h-4" />
                  {t.invoice.custom}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClientDialog
        isOpen={showClientDialog}
        onClose={() => setShowClientDialog(false)}
        clients={clients}
        onAddClient={addClient}
        onUpdateClient={updateClientLocal}
        onDeleteClient={deleteClientLocal}
        initialData={{
          name: draft.buyerName,
          address: draft.buyerAddress,
          email: draft.buyerEmail,
          phone: draft.buyerPhone,
          taxId: draft.buyerTaxId,
          buyerId: draft.buyerId,
          isVatPayer: draft.buyerIsVatPayer,
        }}
      />

      <ItemsDialog
        isOpen={showItemsDialog}
        onClose={() => setShowItemsDialog(false)}
        savedItems={savedItems}
        onAddItem={addSavedItem}
        onUpdateItem={updateSavedItem}
        onDeleteItem={deleteSavedItem}
      />

      <PaymentMethodDialog
        isOpen={showPaymentMethodDialog}
        onClose={() => setShowPaymentMethodDialog(false)}
        paymentMethods={savedPaymentMethods}
        onAddPaymentMethod={savePaymentMethod}
        onUpdatePaymentMethod={updatePaymentMethod}
        onDeletePaymentMethod={deletePaymentMethod}
        initialData={(() => {
          const bankMethod = draft.paymentOptions?.find(
            (po) => po.type === "bank",
          );
          if (bankMethod) {
            return {
              type: "bank",
              title: bankMethod.title,
              accountNumber: bankMethod.accountNumber,
              iban: bankMethod.iban,
              bic: bankMethod.bic,
              bankName: bankMethod.bankName,
              bankAddress: bankMethod.bankAddress,
              variableSymbol: bankMethod.variableSymbol,
              constantSymbol: bankMethod.constantSymbol,
              specificSymbol: bankMethod.specificSymbol,
            };
          }
          return undefined;
        })()}
      />

      <InvoiceNumberTemplate
        isOpen={showInvoiceTemplateModal}
        onClose={() => setShowInvoiceTemplateModal(false)}
        onSave={async (template, scope, previewFirst) => {
          setInvoiceTemplate(template);
          setInvoiceTemplateScope(scope);
          setDraft((d) => ({ ...d, invoiceNo: previewFirst || d.invoiceNo }));
          try {
            const response = await authenticatedFetch(
              "/api/invoice-templates/presets",
            );
            if (response.ok) {
              const data = await response.json();
              setCustomPresets(data);
            }
          } catch (error) {
            console.error(error);
          }
        }}
        onApply={async (template, scope, previewFirst) => {
          setInvoiceTemplate(template);
          setInvoiceTemplateScope(scope);
          setDraft((d) => ({ ...d, invoiceNo: previewFirst || d.invoiceNo }));
        }}
        customPresets={customPresets}
        onDeletePreset={async (template) => {
          try {
            const encodedTemplate = encodeURIComponent(template);
            const response = await authenticatedFetch(
              `/api/invoice-templates/presets/${encodedTemplate}`,
              { method: "DELETE" },
            );
            if (response.ok) {
              const presetsResponse = await authenticatedFetch(
                "/api/invoice-templates/presets",
              );
              if (presetsResponse.ok) {
                setCustomPresets(await presetsResponse.json());
              }
            } else {
              const err = await response.json();
              throw new Error(err.error || "Failed to delete preset");
            }
          } catch (error: any) {
            console.error(error);
            alert(error.message || "Error deleting preset");
            throw error; // Rethrow to prevent closing modal in child
          }
        }}
        currentCustomer={draft.buyerName}
        currentProject=""
      />

      {showOnboarding && (
        <OnboardingTour
          onComplete={handleOnboardingComplete}
          forceStart={localStorage.getItem(LS_ONBOARDING_COMPLETED) === "true"}
        />
      )}

      {showEmailModal && currentEmailInvoice && (
        <EmailConfirmationModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          invoice={currentEmailInvoice}
          initialSubject={emailInitialData.subject}
          initialText={emailInitialData.text}
          onSend={handleSendEmailManual}
          t={t}
          locale={locale}
          type="manual"
          isEmailConfigured={isEmailConfigured}
        />
      )}
      {/* Share Invoice Modal */}
      <AnimatePresence>
        {showShareModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200] p-4"
            onMouseDown={(e) =>
              setMouseDownOnOverlay(e.target === e.currentTarget)
            }
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowShareModal(false);
                setLinkCopied(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight">
                  {locale === "ru"
                    ? "Поделиться счетом"
                    : locale === "cs"
                      ? "Sdílet fakturu"
                      : "Share Invoice"}
                </h2>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setLinkCopied(false);
                  }}
                  className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 text-center overflow-y-auto custom-scrollbar">
                {!generatedShareUrl ? (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400 shadow-inner shrink-0">
                      <Lock size={28} className="sm:w-10 sm:h-10" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-base text-slate-600 dark:text-slate-400 font-medium mb-3 sm:mb-4 leading-snug">
                        {locale === "ru"
                          ? "Установите пароль для просмотра этого счета. Только люди с паролем смогут увидеть PDF."
                          : locale === "cs"
                            ? "Nastavte heslo pro zobrazení této faktury. Pouze lidé s heslem uvidí PDF."
                            : "Set a password to view this invoice. Only people with the password will see the PDF."}
                      </p>
                      <input
                        type="password"
                        placeholder={
                          locale === "ru"
                            ? "Введите пароль"
                            : locale === "cs"
                              ? "Zadejte heslo"
                              : "Enter password"
                        }
                        value={sharePassword}
                        onChange={(e) => setSharePassword(e.target.value)}
                        className="w-full px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/50 outline-none text-center font-bold text-sm sm:text-base"
                      />
                    </div>
                    <button
                      onClick={handleGenerateShare}
                      disabled={!sharePassword || isGeneratingShare}
                      className="w-full py-3 sm:py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl sm:rounded-2xl font-black shadow-lg shadow-emerald-500/25 transition-all disabled:opacity-50 text-xs sm:text-base uppercase tracking-wider"
                    >
                      {isGeneratingShare ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                      ) : locale === "ru" ? (
                        "Создать защищенную ссылку"
                      ) : locale === "cs" ? (
                        "Vytvořit zabezpečený odkaz"
                      ) : (
                        "Create secure link"
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto text-blue-600 dark:text-blue-400 shadow-inner shrink-0">
                      <ShieldCheck size={28} className="sm:w-10 sm:h-10" />
                    </div>
                    <div className="space-y-4 sm:space-y-6">
                      <p className="text-emerald-600 dark:text-emerald-400 font-black text-sm sm:text-base uppercase tracking-tight">
                        {locale === "ru"
                          ? "Ссылка создана!"
                          : locale === "cs"
                            ? "Odkaz vytvořen!"
                            : "Link created!"}
                      </p>

                      <div className="space-y-3">
                        <div className="text-left">
                          <label className="text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">
                            {locale === "ru"
                              ? "Ссылка на счет"
                              : locale === "cs"
                                ? "Odkaz na fakturu"
                                : "Invoice Link"}
                          </label>
                          <div className="relative group">
                            <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-slate-100 dark:border-slate-700 break-all text-[10px] sm:text-xs font-mono text-slate-600 dark:text-slate-400 pr-10 sm:pr-12 leading-relaxed">
                              {generatedShareUrl}
                            </div>
                            <button
                              onClick={() => {
                                if (generatedShareUrl) {
                                  navigator.clipboard.writeText(
                                    generatedShareUrl,
                                  );
                                  setUrlCopied(true);
                                  setTimeout(() => setUrlCopied(false), 2000);
                                }
                              }}
                              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg sm:rounded-xl transition-colors text-slate-400 hover:text-indigo-600"
                            >
                              {urlCopied ? (
                                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                              ) : (
                                <CopyIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="text-left">
                          <label className="text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">
                            {t.landing.auth.password}
                          </label>
                          <div className="relative group">
                            <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-slate-100 dark:border-slate-700 break-all text-[10px] sm:text-xs font-mono text-slate-600 dark:text-slate-400 pr-10 sm:pr-12">
                              {sharePassword}
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(sharePassword);
                                setPasswordCopied(true);
                                setTimeout(
                                  () => setPasswordCopied(false),
                                  2000,
                                );
                              }}
                              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg sm:rounded-xl transition-colors text-slate-400 hover:text-indigo-600"
                            >
                              {passwordCopied ? (
                                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                              ) : (
                                <CopyIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2.5 sm:gap-3 pt-1 sm:pt-2">
                        <button
                          onClick={() => {
                            const fullText = `${generatedShareUrl}\nPassword: ${sharePassword}`;
                            navigator.clipboard.writeText(fullText);
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2000);
                          }}
                          className={`w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-sm transition-all flex items-center justify-center gap-2 ${
                            linkCopied
                              ? "bg-emerald-500 text-white shadow-emerald-500/20"
                              : "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-sm"
                          }`}
                        >
                          {linkCopied ? (
                            <>
                              <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              {locale === "ru"
                                ? "Скопировано!"
                                : locale === "cs"
                                  ? "Zkopírováno!"
                                  : "Copied!"}
                            </>
                          ) : locale === "ru" ? (
                            "Скопировать ссылку и пароль"
                          ) : locale === "cs" ? (
                            "Kopírovat odkaz a heslo"
                          ) : (
                            "Copy link and password"
                          )}
                        </button>

                        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                          <a
                            href={`https://wa.me/?text=${encodeURIComponent(
                              `${generatedShareUrl}\nPassword: ${sharePassword}`,
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 sm:px-4 bg-[#25D366] hover:bg-[#20ba56] text-white rounded-xl sm:rounded-2xl font-bold text-[10px] sm:text-xs transition-all shadow-md"
                          >
                            <svg
                              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.353-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            WhatsApp
                          </a>
                          <a
                            href={`https://t.me/share/url?url=${encodeURIComponent(
                              generatedShareUrl,
                            )}&text=${encodeURIComponent(
                              `Password: ${sharePassword}`,
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 sm:px-4 bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-xl sm:rounded-2xl font-bold text-[10px] sm:text-xs transition-all shadow-md"
                          >
                            <svg
                              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.63.15-.16 2.72-2.49 2.77-2.69.01-.03.01-.14-.07-.18-.08-.04-.19-.01-.27-.01-.12.02-1.92 1.24-5.43 3.61-.51.35-.98.52-1.4.51-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.39-.88.03-.24.45-.48 1.27-.74 4.96-2.16 8.27-3.59 9.93-4.29.68-.29 1.42-.34 1.91-.34.11 0 .35.02.51.05.16.03.35.11.46.25.11.14.14.32.12.48z" />
                            </svg>
                            Telegram
                          </a>
                        </div>

                        {canShare && (
                          <button
                            onClick={async () => {
                              try {
                                await navigator.share({
                                  title:
                                    locale === "ru"
                                      ? "Счет-фактура"
                                      : locale === "cs"
                                        ? "Faktura"
                                        : "Invoice",
                                  text: `${
                                    locale === "ru"
                                      ? "Пароль"
                                      : locale === "cs"
                                        ? "Heslo"
                                        : "Password"
                                  }: ${sharePassword}`,
                                  url: generatedShareUrl || "",
                                });
                              } catch (err) {
                                console.error("Error sharing:", err);
                              }
                            }}
                            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs transition-all flex items-center justify-center gap-2 hover:bg-slate-200 dark:hover:bg-slate-700"
                          >
                            <Share className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            {locale === "ru"
                              ? "Поделиться"
                              : locale === "cs"
                                ? "Sdílet"
                                : "Share"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        <SettingsDialog
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          showSellerSettings={settingsDialogTab === "seller"}
          onSellerSettingsOpen={() => {
            setSettingsDialogTab("seller");
            setActiveTab("seller");
            // Scroll to parties section
            const el = document.getElementById("tour-parties");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
        />
      </AnimatePresence>
    </div>
  );
}

function CopyIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="9"
        y="9"
        width="13"
        height="13"
        rx="2"
        ry="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
